import { Component, useEffect, useMemo, useRef, useState, type ErrorInfo, type ReactNode } from "react";
import {
  Bot,
  CircleCheck,
  Check,
  ChevronRight,
  Copy,
  Coins,
  Download,
  FileUp,
  FileText,
  GalleryHorizontalEnd,
  History,
  Image as ImageIcon,
  Loader2,
  Mic,
  MessageSquareText,
  Paperclip,
  Palette,
  Search,
  Settings,
  SlidersHorizontal,
  Sparkles,
  Send,
  Share2,
  Trash2,
  Upload,
  User,
  Video,
  Volume2,
  Wand2,
  PenLine,
  Flame,
  Zap,
  Award
} from "lucide-react";
import {
  ApiError,
  createDraft,
  createReferenceDraftFromTask,
  deleteTask,
  estimateDraft,
  apiUrl,
  fetchBalance,
  fetchCapabilities,
  fetchCurrentAccount,
  fetchDraft,
  fetchModels,
  fetchTask,
  fetchTasks,
  fetchTransactions,
  generateDraft,
  initTelegramSession,
  updateDraft,
  uploadAttachment,
  fetchTaskActions,
  executeResultAction,
  promptBuilderGenerate,
  fetchModelsStats,
  type CapabilityVisibility,
  type Draft,
  type Task,
  type Transaction,
  type ResultAction,
  type ModelsStats
} from "./lib/api";
import { filterModels, sortModels, supportsCapability, filterCatalogModels, type Capability, type ModelItem } from "./lib/catalog";
import { detectRuntime, getAuthHeaders, getTelegramInitData, getTelegramWebApp } from "./lib/runtime";

type Screen = "create" | "gallery" | "models" | "balance" | "settings";
type ResultType = "image" | "text" | "prompt" | "video" | "audio";
type ScenarioId =
  | "image.text_to_image"
  | "image.image_to_image"
  | "text.write"
  | "text.improve"
  | "text.shorten"
  | "text.translate"
  | "text.continue"
  | "prompt.universal"
  | "prompt.image"
  | "prompt.flux"
  | "prompt.video"
  | "prompt.json_image"
  | "prompt.json_video"
  | "prompt.research"
  | "video.text_to_video"
  | "video.image_to_video"
  | "audio.text_to_speech"
  | "audio.speech_to_speech"
  | "audio.transcription";
type ResultTypeDefinition = {
  id: ResultType;
  title: string;
  description: string;
  icon: ReactNode;
};
type CreationScenario = {
  id: ScenarioId;
  resultType: ResultType;
  capability: Capability;
  title: string;
  description: string;
  inputKind: "prompt" | "reference";
};
type DeepLinkAction = "upload" | "crop" | "models" | null;

const resultTypeDefinitions: ResultTypeDefinition[] = [
  {
    id: "image",
    title: "Изображение",
    description: "Сцена, объект и стиль — по описанию или образцу.",
    icon: <ImageIcon size={20} />
  },
  {
    id: "text",
    title: "Текст",
    description: "Посты, письма, переводы и редактирование текстов.",
    icon: <MessageSquareText size={20} />
  },
  {
    id: "video",
    title: "Видео",
    description: "Короткие ролики по тексту или готовому изображению.",
    icon: <Video size={20} />
  },
  {
    id: "prompt",
    title: "Промпт",
    description: "Точные запросы для изображений, видео и анализа.",
    icon: <PenLine size={20} />
  },
  {
    id: "audio",
    title: "Аудио",
    description: "Озвучивайте текст, меняйте голос, расшифровывайте записи.",
    icon: <Volume2 size={20} />
  }
];

const scenarioDefinitions: CreationScenario[] = [
  {
    id: "image.text_to_image",
    resultType: "image",
    capability: "image.text_to_image",
    title: "По описанию",
    description: "Сцена, объект и детали — обычным текстом.",
    inputKind: "prompt"
  },
  {
    id: "image.image_to_image",
    resultType: "image",
    capability: "image.image_to_image",
    title: "По образцу",
    description: "Добавьте образец и укажите нужные изменения.",
    inputKind: "reference"
  },
  {
    id: "text.write",
    resultType: "text",
    capability: "text.chat",
    title: "Написать с нуля",
    description: "Напишите пост, письмо, описание, сценарий или объявление.",
    inputKind: "prompt"
  },
  {
    id: "text.improve",
    resultType: "text",
    capability: "text.chat",
    title: "Улучшить текст",
    description: "Исправьте ошибки, улучшите стиль и сделайте текст яснее.",
    inputKind: "prompt"
  },
  {
    id: "text.shorten",
    resultType: "text",
    capability: "text.chat",
    title: "Сократить",
    description: "Сократите текст, выделите главное или подготовьте тезисы.",
    inputKind: "prompt"
  },
  {
    id: "text.translate",
    resultType: "text",
    capability: "text.chat",
    title: "Перевести",
    description: "Переведите текст, сохранив смысл, тон и форматирование.",
    inputKind: "prompt"
  },
  {
    id: "text.continue",
    resultType: "text",
    capability: "text.chat",
    title: "Продолжить",
    description: "Продолжите текст в том же стиле и настроении.",
    inputKind: "prompt"
  },
  {
    id: "prompt.universal",
    resultType: "prompt",
    capability: "text.prompt_builder",
    title: "Универсальный промпт",
    description: "Создание точных запросов для любых нейросетей.",
    inputKind: "prompt"
  },
  {
    id: "prompt.image",
    resultType: "prompt",
    capability: "text.prompt_builder",
    title: "Промпт для изображения",
    description: "Сцена, композиция, свет и стиль для изображения.",
    inputKind: "prompt"
  },
  {
    id: "prompt.flux",
    resultType: "prompt",
    capability: "text.prompt_builder",
    title: "Промпт под Flux",
    description: "Запросы для Flux: детализация и фотореализм.",
    inputKind: "prompt"
  },
  {
    id: "prompt.video",
    resultType: "prompt",
    capability: "text.prompt_builder",
    title: "Промпт для видео",
    description: "Сцена, движение камеры, длительность и стиль.",
    inputKind: "prompt"
  },
  {
    id: "prompt.json_image",
    resultType: "prompt",
    capability: "text.prompt_builder",
    title: "JSON для изображения",
    description: "Запрос в JSON: объект, фон, стиль, свет и формат.",
    inputKind: "prompt"
  },
  {
    id: "prompt.json_video",
    resultType: "prompt",
    capability: "text.prompt_builder",
    title: "JSON для видео",
    description: "Запрос видео в JSON: сцена, камера, движение и длительность.",
    inputKind: "prompt"
  },
  {
    id: "prompt.research",
    resultType: "prompt",
    capability: "text.prompt_builder",
    title: "Исследование",
    description: "Глубокий анализ, поиск и проверка гипотез.",
    inputKind: "prompt"
  },
  {
    id: "video.text_to_video",
    resultType: "video",
    capability: "video.text_to_video",
    title: "Видео по описанию",
    description: "Создайте короткий ролик по тексту.",
    inputKind: "prompt"
  },
  {
    id: "video.image_to_video",
    resultType: "video",
    capability: "video.image_to_video",
    title: "Видео из изображения",
    description: "Оживите изображение коротким роликом.",
    inputKind: "reference"
  },
  {
    id: "audio.text_to_speech",
    resultType: "audio",
    capability: "audio.text_to_speech",
    title: "Озвучить текст",
    description: "Озвучьте текст естественным голосом.",
    inputKind: "prompt"
  },
  {
    id: "audio.speech_to_speech",
    resultType: "audio",
    capability: "audio.speech_to_speech",
    title: "Изменить голос",
    description: "Сохраните слова и выберите новую подачу.",
    inputKind: "reference"
  },
  {
    id: "audio.transcription",
    resultType: "audio",
    capability: "audio.transcription",
    title: "Расшифровать запись",
    description: "Превратите аудио или голосовое сообщение в текст.",
    inputKind: "reference"
  }
];

const fallbackCapabilities: CapabilityVisibility[] = [
  {
    capability: "image.text_to_image",
    title: "Изображение",
    bot: "enabled",
    webapp: "enabled",
    hybrid: "enabled"
  },
  {
    capability: "text.chat",
    title: "Текст",
    bot: "enabled",
    webapp: "enabled",
    hybrid: "enabled"
  },
  {
    capability: "image.image_to_image",
    title: "По образцу",
    bot: "enabled_basic",
    webapp: "enabled",
    hybrid: "enabled"
  },
  {
    capability: "text.prompt_builder",
    title: "Генератор промптов",
    bot: "enabled",
    webapp: "enabled",
    hybrid: "enabled"
  },
  {
    capability: "video.text_to_video",
    title: "Видео",
    bot: "coming_soon",
    webapp: "coming_soon",
    hybrid: "coming_soon"
  },
  {
    capability: "audio.transcription",
    title: "Аудио",
    bot: "coming_soon",
    webapp: "coming_soon",
    hybrid: "coming_soon"
  }
];

const aspectRatios = ["1:1", "4:3", "3:4", "16:9", "9:16"];
const tones = ["Нейтрально", "Дружелюбно", "Уверенно", "Коротко", "Подробно"];

/**
 * Keep result frames faithful to the format chosen for the generation. The
 * API stores ratios as `width:height`, while CSS accepts the same values with
 * a slash. Unknown/legacy values intentionally fall back to the neutral 4:3
 * card instead of allowing an invalid inline style to collapse the preview.
 */
function parseAspectRatio(value?: unknown) {
  const match = String(value || "").trim().match(/^(\d+(?:\.\d+)?)\s*:\s*(\d+(?:\.\d+)?)$/);
  if (!match || Number(match[1]) <= 0 || Number(match[2]) <= 0) return null;
  return { width: match[1], height: match[2], label: `${match[1]}:${match[2]}` };
}

function cssAspectRatio(value?: string | null) {
  const parsed = parseAspectRatio(value);
  return parsed ? `${parsed.width} / ${parsed.height}` : "4 / 3";
}

function taskAspectRatioLabel(task: Task) {
  const candidates = [
    task.aspect_ratio,
    typeof task.params?.aspect_ratio === "string" ? task.params.aspect_ratio : null,
    typeof task.assets?.[0]?.metadata?.aspect_ratio === "string" ? task.assets[0].metadata.aspect_ratio : null,
  ];
  return candidates.map(parseAspectRatio).find(Boolean)?.label ?? "4:3";
}

function taskAspectRatio(task: Task) {
  return cssAspectRatio(taskAspectRatioLabel(task));
}
type AuthStatus = "checking" | "authenticated" | "guest" | "error";
type WizardStep = "type" | "model" | "subtype" | "input" | "params" | "review";
type GenerationPhase = "idle" | "preparing" | "sending" | "processing" | "saving" | "error";
type TextTurn = {
  id: string;
  role: "user" | "assistant" | "error";
  content: string;
  modelId?: string | null;
  createdAt: string;
  attachments?: Array<{ name: string; mimeType: string; size: number }>;
};
type TextConversation = {
  id: string;
  title: string;
  turns: TextTurn[];
  /** Compact local memory for turns that no longer fit the provider window. */
  summary?: string;
  updatedAt: string;
};
type TextAttachment = { name: string; mimeType: string; size: number };

const backendExecutableCapabilities = new Set<Capability>(["image.text_to_image", "image.image_to_image", "text.chat", "text.prompt_builder"]);
const allowedCapabilities: Capability[] = [
  "image.text_to_image",
  "image.image_to_image",
  "text.chat",
  "text.summarize",
  "text.translate",
  "text.prompt_builder",
  "video.text_to_video",
  "video.image_to_video",
  "audio.text_to_speech",
  "audio.speech_to_speech",
  "audio.transcription"
];
const allowedScreens: Screen[] = ["create", "gallery", "balance", "settings"];
const enabledCapabilityStates = new Set(["enabled", "enabled_basic", "enabled_if_model_available"]);
const textConversationsStorageKey = "znich.text.conversations.v1";

function textConversationsKey(accountId: string): string {
  return `${textConversationsStorageKey}:${accountId}`;
}
const generationUsesAgy = true;
const TEXT_MODEL_LABEL = "Gemini 3.8 Flash";
const IMAGE_MODEL_LABEL = "Nano Banana 2";

function getPublicModelLabel(
  capability: string | null | undefined,
  modelId?: string | null,
  modelName?: string | null,
  fallback?: string,
  forceActiveExecutor = false,
) {
  const normalizedCapability = String(capability || "").toLowerCase();
  const normalizedId = String(modelId || "").trim().toLowerCase();
  const normalizedName = String(modelName || "").trim().toLowerCase();
  const isInternalExecutor = forceActiveExecutor
    || normalizedId === "agy"
    || normalizedId.startsWith("agy/")
    || normalizedName === "agy"
    || normalizedName.startsWith("agy/");
  if (isInternalExecutor && normalizedCapability.startsWith("image.")) return IMAGE_MODEL_LABEL;
  if (isInternalExecutor && (normalizedCapability.startsWith("text.") || normalizedCapability.startsWith("document."))) {
    return TEXT_MODEL_LABEL;
  }
  if (isInternalExecutor) return "Модель Znich";
  const value = String(modelName || modelId || fallback || "").trim();
  return value || fallback || "Автоматический выбор";
}

function isInternalModel(model: ModelItem) {
  const modelId = String(model.model_id || "").trim().toLowerCase();
  const displayName = String(model.display_name || "").trim().toLowerCase();
  return model.provider.toLowerCase() === "agy"
    || modelId === "agy"
    || modelId.startsWith("agy/")
    || displayName === "agy"
    || displayName.startsWith("agy/");
}

function getCatalogModelLabel(model: ModelItem) {
  const capability = model.output_modalities.includes("image") ? "image.text_to_image" : model.output_modalities.includes("text") ? "text.chat" : undefined;
  return getPublicModelLabel(capability, model.model_id, model.display_name, model.display_name || model.model_id);
}

function getPublicTransactionDescription(description?: string | null) {
  if (!description) return null;
  const value = String(description);
  if (/генерация\s+текста/i.test(value) || /создание\s+текста/i.test(value) || /текста\s+через\s+agy/i.test(value)) return `Создание текста: ${TEXT_MODEL_LABEL}`;
  if (/генерация\s+изображения/i.test(value) || /создание\s+изображения/i.test(value) || /трансформация\s+изображения/i.test(value)) {
    return generationUsesAgy ? `Создание изображения: ${IMAGE_MODEL_LABEL}` : value.replace(/\bAGY\b/gi, "сервис генерации");
  }
  return value.replace(/\bAGY\b/gi, "сервис генерации");
}

// AGY's generic result field can contain either ordinary text or a serialized
// response envelope.  Keep this browser-side fallback for legacy assets and
// cached dialogue turns created before the backend started normalizing them.
// Only recognized provider envelopes are unwrapped; deliberate JSON content
// from the prompt builder remains intact.
const textAnswerKeys = ["response", "answer", "text", "content", "output", "message"] as const;
const textResultKeys = ["result", ...textAnswerKeys] as const;
const textEnvelopeMarkers = new Set([
  "conversation_id",
  "request_id",
  "status",
  "duration_seconds",
  "duration_ms",
  "num_turns",
  "usage",
  "input_tokens",
  "output_tokens",
  "thinking_tokens",
  "cache_read_tokens",
  "total_tokens"
]);
const textKnownEnvelopeKeys = new Set([...textEnvelopeMarkers, ...textResultKeys]);
const maxTextJsonCandidateChars = 4 * 1024 * 1024;

function sanitizeTextPayload(value: unknown, depth = 0): string {
  if (depth > 8) return typeof value === "string" ? value.trim() : "";

  if (typeof value === "string") {
    const text = value.trim();
    if (text.length <= maxTextJsonCandidateChars && (text.startsWith("{") || text.startsWith("["))) {
      try {
        const parsed = JSON.parse(text) as unknown;
        if (isTextResponseEnvelope(parsed)) {
          const extracted = sanitizeTextPayload(parsed, depth + 1);
          if (extracted) return extracted;
        }
      } catch {
        // Malformed legacy payloads are displayed as-is instead of breaking
        // the conversation or gallery render.
      }
    }
    return text;
  }

  if (Array.isArray(value)) {
    const parts = value
      .map((item) => sanitizeTextPayload(item, depth + 1))
      .filter(Boolean);
    if (parts.length > 0) return parts.join("\n");
    return safeStructuredText(value);
  }

  if (value && typeof value === "object") {
    const payload = value as Record<string, unknown>;
    if (isTextResponseEnvelope(payload)) {
      for (const key of textResultKeys) {
        if (!(key in payload)) continue;
        const extracted = sanitizeTextPayload(payload[key], depth + 1);
        if (extracted) return extracted;
      }
      return "";
    }
    // Keep intentional structured output (for example a prompt JSON object)
    // readable rather than turning it into [object Object].
    return safeStructuredText(value);
  }

  return value === null || value === undefined ? "" : String(value).trim();
}

function isTextResponseEnvelope(value: unknown): value is Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const keys = Object.keys(value as Record<string, unknown>);
  if (keys.some((key) => textEnvelopeMarkers.has(key))) {
    return keys.some((key) => (textResultKeys as readonly string[]).includes(key));
  }
  return keys.some((key) => (textAnswerKeys as readonly string[]).includes(key))
    && keys.every((key) => textKnownEnvelopeKeys.has(key));
}

function safeStructuredText(value: unknown): string {
  try {
    const serialized = JSON.stringify(value, null, 2);
    return typeof serialized === "string" ? serialized : "";
  } catch {
    return "";
  }
}

function makeTextConversation(title = "Новый диалог"): TextConversation {
  return {
    id: `conversation-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    title,
    turns: [],
    summary: "",
    updatedAt: new Date().toISOString()
  };
}

function readTextConversations(accountId: string | null): TextConversation[] {
  if (typeof window === "undefined") return [];
  try {
    const legacyAllowed = !accountId && Object.keys(getAuthHeaders()).length === 0;
    const stored = accountId
      ? window.localStorage.getItem(textConversationsKey(accountId))
      : legacyAllowed
        ? window.localStorage.getItem(textConversationsStorageKey)
        : null;
    if (!stored) return [makeTextConversation()];
    const parsed = JSON.parse(stored) as TextConversation[];
    if (!Array.isArray(parsed) || parsed.length === 0) return [makeTextConversation()];
    return parsed.slice(0, 12).map((conversation) => ({
      ...conversation,
      summary: typeof conversation.summary === "string" ? sanitizeTextPayload(conversation.summary).slice(-6000) : "",
      turns: Array.isArray(conversation.turns)
        ? conversation.turns.slice(-40).map((turn) => ({
            ...turn,
            // User text is data, not a provider envelope. Sanitizing it here
            // would silently rewrite a JSON request after a reload.
            content: turn?.role === "assistant" || turn?.role === "error"
              ? sanitizeTextPayload(turn?.content)
              : String(turn?.content ?? "")
          }))
        : []
    }));
  } catch {
    return [makeTextConversation()];
  }
}

function buildConversationSummary(previous: string | undefined, archivedTurns: TextTurn[]): string {
  if (archivedTurns.length === 0) return previous || "";
  const archived = archivedTurns.map((turn) => {
    const label = turn.role === "user" ? "Пользователь" : turn.role === "assistant" ? "Ассистент" : "Ошибка";
    const content = turn.content.replace(/\s+/g, " ").trim();
    return `${label}: ${content.slice(0, 900)}${content.length > 900 ? "…" : ""}`;
  });
  return [...(previous ? [previous] : []), ...archived].join("\n").slice(-6000);
}

function buildBoundedTextPrompt(turns: TextTurn[], nextMessage: string, summary = ""): string {
  const maxChars = 28000;
  const selected: string[] = [];
  let used = 0;
  for (const turn of [...turns].reverse()) {
    const label = turn.role === "user" ? "Пользователь" : turn.role === "assistant" ? "Ассистент" : "Система";
    const content = turn.content.length > 6000 ? `${turn.content.slice(0, 6000)}…` : turn.content;
    const line = `${label}: ${content}`;
    if (used + line.length > maxChars) break;
    selected.unshift(line);
    used += line.length;
  }
  const omitted = Math.max(0, turns.length - selected.length);
  const compactSummary = summary
    ? `Краткая память предыдущих сообщений:\n${summary}`
    : omitted > 0
      ? `В начале диалога были ещё ${omitted} реплик. Сохраняй уже согласованные факты и требования; не утверждай, что их нет.`
      : "";
  return [
    "Служебный контекст диалога. Не пересказывай его и не упоминай пользователю:",
    compactSummary,
    ...selected,
    `Пользователь: ${nextMessage}`
  ].filter(Boolean).join("\n\n");
}

type AuthorizedImageProps = {
  source: string | null;
  fallbackSource?: string | null;
  alt: string;
  className?: string;
  loading?: "eager" | "lazy";
};

async function blobToBrowserUrl(blob: Blob): Promise<string | null> {
  if (typeof URL.createObjectURL === "function") {
    try {
      return URL.createObjectURL(blob);
    } catch {
      // Some embedded webviews/test DOMs expose createObjectURL but reject a
      // Blob from a different realm. Fall through to a portable data URL.
    }
  }
  if (typeof FileReader === "undefined") return null;
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === "string" ? reader.result : null);
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(blob);
  });
}

/**
 * Asset previews are protected by the same Telegram/dev header as the JSON
 * API. A regular <img src> request cannot carry that header, so fetch the
 * bytes explicitly and expose a short-lived object URL to the browser.
 */
function useAuthorizedAsset(source: string | null, fallbackSource: string | null = null) {
  const [reloadKey, setReloadKey] = useState(0);
  const [state, setState] = useState<{ src: string | null; loading: boolean; failed: boolean }>({
    src: null,
    loading: Boolean(source || fallbackSource),
    failed: false
  });

  useEffect(() => {
    let active = true;
    let objectUrl: string | null = null;

    const candidates = [source, fallbackSource].filter((value, index, values): value is string => Boolean(value) && values.indexOf(value) === index);
    if (candidates.length === 0) {
      setState({ src: null, loading: false, failed: false });
      return () => {
        active = false;
      };
    }

    // Local uploads already have a browser-readable URL. Do not round-trip
    // those through the API, while still using the same component everywhere.
    if (candidates[0].startsWith("blob:") || candidates[0].startsWith("data:")) {
      setState({ src: candidates[0], loading: false, failed: false });
      return () => {
        active = false;
      };
    }

    setState({ src: null, loading: true, failed: false });
    const controller = new AbortController();
    const headers = new Headers(getAuthHeaders());
    (async () => {
      for (const candidate of candidates) {
        try {
          const response = await fetch(candidate, { headers, signal: controller.signal });
          if (!response.ok) throw new Error(`asset-${response.status}`);
          const blob = await response.blob();
          if (!blob.size) throw new Error("asset-empty");
          if (!active) return;
          objectUrl = await blobToBrowserUrl(blob);
          if (!objectUrl) throw new Error("asset-url-unavailable");
          setState({ src: objectUrl, loading: false, failed: false });
          return;
        } catch (error: unknown) {
          if (error instanceof Error && error.name === "AbortError") return;
          // A preview can expire independently from the original. Try the
          // full download endpoint before showing an error placeholder.
        }
      }
      if (active) setState({ src: null, loading: false, failed: true });
    })();

    return () => {
      active = false;
      controller.abort();
      if (objectUrl?.startsWith("blob:") && typeof URL.revokeObjectURL === "function") URL.revokeObjectURL(objectUrl);
    };
  }, [source, fallbackSource, reloadKey]);

  return {
    ...state,
    retry: () => setReloadKey((current) => current + 1),
    markFailed: () => setState({ src: null, loading: false, failed: true })
  };
}

function AuthorizedImage({ source, fallbackSource = null, alt, className, loading = "lazy" }: AuthorizedImageProps) {
  const { src, loading: isLoading, failed, retry, markFailed } = useAuthorizedAsset(source, fallbackSource);

  if (isLoading) {
    return (
      <div className="authorized-image-state loading" role="status" aria-label="Загружаем изображение">
        <Loader2 className="spin" size={22} aria-hidden="true" />
        <span>Загружаем изображение…</span>
      </div>
    );
  }

  if (failed || !src) {
    return (
      <div className="authorized-image-state error" role="img" aria-label="Предпросмотр недоступен">
        <ImageIcon size={22} aria-hidden="true" />
        <span>Не удалось загрузить файл</span>
        {(source || fallbackSource) && <button type="button" className="secondary compact-button" onClick={retry}>Повторить</button>}
      </div>
    );
  }

  return <img src={src} alt={alt} className={className} loading={loading} decoding="async" onError={markFailed} />;
}

function useBodyScrollLock(locked: boolean) {
  useEffect(() => {
    if (!locked || typeof document === "undefined") return;
    const body = document.body;
    const documentElement = document.documentElement;
    const appShell = document.querySelector<HTMLElement>(".app-shell");
    // `#root` is present in the shipped Mini App. The fallback keeps the hook
    // correct in embedded previews/tests that mount App into another host.
    const root = document.getElementById("root") ?? appShell?.parentElement ?? null;
    const main = appShell?.querySelector<HTMLElement>(".main") ?? null;
    const scrollY = window.scrollY;
    const lockTargets = [documentElement, body, root, appShell, main].filter(
      (element, index, all): element is HTMLElement => Boolean(element) && all.indexOf(element) === index,
    );
    const previousTargets = lockTargets.map((element) => ({
      element,
      overflow: element.style.overflow,
      overflowX: element.style.overflowX,
      overflowY: element.style.overflowY,
      overscrollBehavior: element.style.overscrollBehavior,
      overscrollBehaviorX: element.style.overscrollBehaviorX,
      overscrollBehaviorY: element.style.overscrollBehaviorY,
    }));
    const previousBody = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
    };

    body.classList.add("modal-open");
    documentElement.classList.add("modal-open");
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";

    // Telegram's WebView can keep scrolling a nested application container
    // even when the document itself is fixed. Lock the root, shell and main
    // scrollports as well, while leaving the modal's own scroll area intact.
    for (const element of lockTargets) {
      element.style.overflow = "hidden";
      element.style.overflowX = "hidden";
      element.style.overflowY = "hidden";
      element.style.overscrollBehavior = "none";
      element.style.overscrollBehaviorX = "none";
      element.style.overscrollBehaviorY = "none";
    }

    // Some iOS/Telegram versions route touch gestures to the page before CSS
    // overflow rules take effect. Prevent gestures outside a dialog, but keep
    // vertical scrolling available inside the opened result/model dialog.
    const preventBackgroundScroll = (event: TouchEvent | WheelEvent) => {
      if (!event.cancelable) return;
      const target = event.target;
      const element = target instanceof Element ? target : null;
      if (element?.closest(".modal-content")) return;
      event.preventDefault();
    };
    document.addEventListener("touchmove", preventBackgroundScroll, { passive: false });
    document.addEventListener("wheel", preventBackgroundScroll, { passive: false });

    return () => {
      document.removeEventListener("touchmove", preventBackgroundScroll);
      document.removeEventListener("wheel", preventBackgroundScroll);
      body.classList.remove("modal-open");
      documentElement.classList.remove("modal-open");
      body.style.position = previousBody.position;
      body.style.top = previousBody.top;
      body.style.left = previousBody.left;
      body.style.right = previousBody.right;
      body.style.width = previousBody.width;
      for (const previousTarget of previousTargets) {
        previousTarget.element.style.overflow = previousTarget.overflow;
        previousTarget.element.style.overflowX = previousTarget.overflowX;
        previousTarget.element.style.overflowY = previousTarget.overflowY;
        previousTarget.element.style.overscrollBehavior = previousTarget.overscrollBehavior;
        previousTarget.element.style.overscrollBehaviorX = previousTarget.overscrollBehaviorX;
        previousTarget.element.style.overscrollBehaviorY = previousTarget.overscrollBehaviorY;
      }
      // jsdom does not implement scrollTo; in the browser restoring the
      // captured position prevents a modal from jumping the gallery.
      if (scrollY > 0 && typeof window.scrollTo === "function") window.scrollTo(0, scrollY);
    };
  }, [locked]);
}

function useDialogFocus(locked: boolean) {
  const restoreTarget = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!locked || typeof document === "undefined") {
      if (!locked) {
        const target = restoreTarget.current;
        restoreTarget.current = null;
        if (target && document.contains(target) && !target.matches(":disabled")) target.focus();
      }
      return;
    }

    const dialog = document.querySelector<HTMLElement>('[role="dialog"][aria-modal="true"]');
    if (!dialog) return;

    restoreTarget.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const selector = [
      "button:not(:disabled)",
      "a[href]",
      "input:not(:disabled)",
      "select:not(:disabled)",
      "textarea:not(:disabled)",
      "[tabindex]:not([tabindex=\"-1\"]):not([aria-disabled=\"true\"])",
    ].join(",");

    const getFocusable = () => Array.from(dialog.querySelectorAll<HTMLElement>(selector));
    const focusFirst = () => getFocusable()[0]?.focus();
    const focusTimer = window.setTimeout(focusFirst, 0);
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Tab") return;
      const focusable = getFocusable();
      if (focusable.length === 0) {
        event.preventDefault();
        dialog.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [locked]);
}

export function App() {
  const runtime = detectRuntime();
  const tg = getTelegramWebApp();
  const hasAuthHeaders = Object.keys(getAuthHeaders()).length > 0;
  const [authStatus, setAuthStatus] = useState<AuthStatus>(hasAuthHeaders ? "checking" : "guest");
  const isAuthenticated = authStatus === "authenticated";
  
  const [screen, setScreen] = useState<Screen>("create");
  // Creation starts as an intentional, neutral choice. A type is selected
  // only after the user taps a card (or follows an explicit deep link).
  const [step, setStep] = useState<WizardStep>("type");
  const [resultType, setResultType] = useState<ResultType | null>(null);
  const [scenarioId, setScenarioId] = useState<ScenarioId | null>(null);
  const [capability, setCapability] = useState<Capability | null>(null);
  const [prompt, setPrompt] = useState("");
  const [aspectRatio, setAspectRatio] = useState("1:1");
  const [textTone, setTextTone] = useState("Нейтрально");
  const [promptTargetCapability, setPromptTargetCapability] = useState<Capability>("image.text_to_image");
  const [selectedModel, setSelectedModel] = useState<string | null>(null);
  const [modelSelectionMode, setModelSelectionMode] = useState<"auto" | "trend" | "cheap" | "fast" | "quality" | "manual">("auto");
  const [showModelOverlay, setShowModelOverlay] = useState(false);
  const [seed, setSeed] = useState<string>("");
  const [negativePrompt, setNegativePrompt] = useState<string>("");
  const [nVariants, setNVariants] = useState<number>(1);
  const [imageQuality, setImageQuality] = useState<string>("standard");
  const [models, setModels] = useState<ModelItem[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [balance, setBalance] = useState<string | null>(null);
  const [telegramProfile, setTelegramProfile] = useState<{ username?: string | null; fullName?: string | null }>({});
  const [capabilityItems, setCapabilityItems] = useState<CapabilityVisibility[]>(fallbackCapabilities);
  const [loadingWorkspace, setLoadingWorkspace] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);

  useBodyScrollLock(Boolean(selectedTask || showModelOverlay));
  useDialogFocus(Boolean(selectedTask || showModelOverlay));
  
  // Catalog filters
  const [query, setQuery] = useState("");
  const [provider, setProvider] = useState("all");
  const [sort, setSort] = useState("recommended");
  
  const [uploadPreview, setUploadPreview] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deepLinkAction, setDeepLinkAction] = useState<DeepLinkAction>(null);
  const [busy, setBusy] = useState(false);
  const [generationPhase, setGenerationPhase] = useState<GenerationPhase>("idle");
  const [generationStartedAt, setGenerationStartedAt] = useState<number | null>(null);
  const [generationElapsedSeconds, setGenerationElapsedSeconds] = useState(0);
  const [estimating, setEstimating] = useState(false);
  const [textInput, setTextInput] = useState("");
  const [textStorageAccountId, setTextStorageAccountId] = useState<string | null>(null);
  const [textConversations, setTextConversations] = useState<TextConversation[]>(() => (
    // Do not render an old unscoped conversation while Telegram auth is being
    // resolved. Guest-only local development may still use the legacy key for
    // compatibility, but an authenticated browser starts empty until its
    // account-scoped history is loaded below.
    hasAuthHeaders || getTelegramInitData()
      ? [makeTextConversation()]
      : readTextConversations(null)
  ));
  const [activeTextConversationId, setActiveTextConversationId] = useState("");
  const [textAttachments, setTextAttachments] = useState<TextAttachment[]>([]);
  const [promptOutput, setPromptOutput] = useState("");
  const [promptBusy, setPromptBusy] = useState(false);

  useEffect(() => {
    if (!busy || generationStartedAt === null) {
      if (!busy) setGenerationElapsedSeconds(0);
      return;
    }
    const updateElapsed = () => {
      setGenerationElapsedSeconds(Math.max(0, Math.floor((Date.now() - generationStartedAt) / 1000)));
    };
    updateElapsed();
    const timer = window.setInterval(updateElapsed, 1000);
    return () => window.clearInterval(timer);
  }, [busy, generationStartedAt]);

  useEffect(() => {
    if (!showModelOverlay && !selectedTask) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      if (showModelOverlay) setShowModelOverlay(false);
      if (selectedTask) setSelectedTask(null);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [showModelOverlay, selectedTask]);

  const activeTextConversation = useMemo(
    () => textConversations.find((conversation) => conversation.id === activeTextConversationId) ?? textConversations[0] ?? makeTextConversation(),
    [textConversations, activeTextConversationId]
  );
  const textTurns = activeTextConversation.turns;

  useEffect(() => {
    try {
      if (typeof window !== "undefined") {
        if (textStorageAccountId) {
          window.localStorage?.setItem(textConversationsKey(textStorageAccountId), JSON.stringify(textConversations));
        }
      }
    } catch {
      // Some embedded/opaque webviews do not expose localStorage.
    }
    if (!activeTextConversationId || !textConversations.some((conversation) => conversation.id === activeTextConversationId)) {
      setActiveTextConversationId(textConversations[0]?.id ?? "");
    }
  }, [textConversations, activeTextConversationId, textStorageAccountId]);

  useEffect(() => {
    tg?.ready?.();
    tg?.expand?.();
  }, [tg]);

  useEffect(() => {
    void bootstrapAuth();
    void loadCapabilities();
  }, []);

  async function loadCapabilities() {
    try {
      const items = await fetchCapabilities();
      // A transient empty response should not turn every card into a dead
      // end. Keep the known-safe capability map until the server returns a
      // real list.
      setCapabilityItems(items.length > 0 ? items : fallbackCapabilities);
    } catch {
      setCapabilityItems(fallbackCapabilities);
    }
  }

  async function bootstrapAuth() {
    const initData = getTelegramInitData();
    if (initData) {
      setAuthStatus("checking");
      try {
        const session = await initTelegramSession(initData);
        const accountId = session.account.id;
        setTextStorageAccountId(accountId);
        setTextConversations(readTextConversations(accountId));
        setBalance(session.balance?.token_balance ?? session.account.token_balance ?? null);
        setTelegramProfile({
          username: session.account.telegram_username,
          fullName: session.account.telegram_full_name
        });
        setAuthStatus("authenticated");
        return;
      } catch (err) {
        setAuthStatus("error");
        setError("Не удалось войти через Telegram. Перезапустите приложение из бота.");
        return;
      }
    }
    if (hasAuthHeaders) {
      try {
        const session = await fetchCurrentAccount();
        const accountId = session.account.id;
        setTextStorageAccountId(accountId);
        setTextConversations(readTextConversations(accountId));
        setBalance(session.account.token_balance ?? null);
        setTelegramProfile({
          username: session.account.telegram_username,
          fullName: session.account.telegram_full_name
        });
        setAuthStatus("authenticated");
      } catch {
        setAuthStatus("error");
        setError("Не удалось загрузить профиль. Откройте приложение заново.");
      }
      return;
    }
    setAuthStatus("guest");
  }

  // Handle URL deep links & load workspace
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const screenParam = params.get("screen");
    const capabilityParam = params.get("capability");
    const taskId = params.get("task_id");
    const draftId = params.get("draft_id");
    const actionParam = params.get("action");
    const action = actionParam === "upload" || actionParam === "crop" ? actionParam : null;
    setDeepLinkAction(action);

    if (screenParam && allowedScreens.includes(screenParam as Screen)) {
      setScreen(screenParam as Screen);
    }
    if (
      capabilityParam &&
      allowedCapabilities.includes(capabilityParam as Capability) &&
      runnableCapabilities.has(capabilityParam as Capability)
    ) {
      selectScenarioByCapability(capabilityParam as Capability);
      setStep("input");
    }
    if (taskId) {
      setScreen("gallery");
      if (isAuthenticated) {
        void loadTaskFromLink(taskId);
      } else {
      setStatus("Откройте Znich в Telegram, чтобы посмотреть этот результат.");
      }
    }
    if (action === "upload" || action === "crop") {
      setScreen("create");
      if (runnableCapabilities.has("image.image_to_image")) {
        selectScenario("image.image_to_image");
        setStep("input");
        setStatus(
          action === "crop"
            ? "Обрезка пока недоступна. Загрузите образец и опишите, что изменить."
            : "Открыли загрузку образца для черновика."
        );
      } else {
        setResultType(null);
        setScenarioId(null);
        setCapability(null);
        setStep("type");
        setError("Создание по образцу пока недоступно. Выберите другой формат.");
      }
    }
    if (draftId && isAuthenticated) {
      void loadDraftFromLink(draftId);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;
    void loadWorkspaceData();
  }, [isAuthenticated]);

  const resolvedModelId = useMemo(() => {
    if (generationUsesAgy) return null;
    if (!capability) return null;
    if (modelSelectionMode === "auto") return null;
    if (modelSelectionMode === "manual") return selectedModel;
    const filtered = models.filter((m) => supportsCapability(m, capability) && m.is_available);
    if (filtered.length === 0) return null;
    if (modelSelectionMode === "cheap") {
      const sorted = [...filtered].sort((a, b) => Number(a.estimated_cost) - Number(b.estimated_cost));
      return sorted[0].model_id;
    }
    if (modelSelectionMode === "fast") {
      const sorted = [...filtered].sort((a, b) => b.speed_score - a.speed_score);
      return sorted[0].model_id;
    }
    if (modelSelectionMode === "quality" || modelSelectionMode === "trend") {
      const sorted = [...filtered].sort((a, b) => b.quality_score - a.quality_score);
      return sorted[0].model_id;
    }
    return null;
  }, [modelSelectionMode, selectedModel, models, capability]);

  const currentModel = useMemo(() => models.find((model) => model.model_id === resolvedModelId) ?? null, [models, resolvedModelId]);

  const runnableCapabilities = useMemo(() => {
    const next = new Set<Capability>();
    for (const item of capabilityItems) {
      if (
        backendExecutableCapabilities.has(item.capability as Capability) &&
        enabledCapabilityStates.has(item.webapp)
      ) {
        next.add(item.capability as Capability);
      }
    }
    return next;
  }, [capabilityItems]);
  const availableScenarios = useMemo(() => {
    const list = scenarioDefinitions.filter((scenario) => runnableCapabilities.has(scenario.capability));
    if (!currentModel) return list;
    return list.filter((scenario) => supportsCapability(currentModel, scenario.capability));
  }, [runnableCapabilities, currentModel]);
  const availableResultTypes = useMemo(
    () => resultTypeDefinitions.filter((type) => availableScenarios.some((scenario) => scenario.resultType === type.id)),
    [availableScenarios]
  );

  useEffect(() => {
    // Capability discovery must never choose a type for the user. If an
    // already selected type becomes unavailable, clear its stale selection.
    if (!resultType || availableResultTypes.length === 0) return;
    if (availableResultTypes.some((type) => type.id === resultType)) return;
    setResultType(null);
    setScenarioId(null);
    setCapability(null);
    setStep("type");
  }, [availableResultTypes, resultType]);

  const activeScenario = useMemo(
    () => scenarioId ? scenarioDefinitions.find((scenario) => scenario.id === scenarioId) ?? null : null,
    [scenarioId]
  );
  const activeTypeScenarios = useMemo(
    () => resultType ? availableScenarios.filter((scenario) => scenario.resultType === resultType) : [],
    [availableScenarios, resultType]
  );

  useEffect(() => {
    // Capability discovery can change after the first render. Never leave a
    // stale route selected when the server hides or disables it; return the
    // user to the next valid decision instead of showing an empty form.
    if (!scenarioId || availableScenarios.some((scenario) => scenario.id === scenarioId)) return;
    setScenarioId(null);
    setCapability(null);
    if (resultType === "image" || resultType === "video" || resultType === "audio") {
      setStep("subtype");
    }
  }, [availableScenarios, resultType, scenarioId]);

  function selectScenario(nextScenarioId: ScenarioId, options?: { preserveInput?: boolean }) {
    const scenario = scenarioDefinitions.find((item) => item.id === nextScenarioId);
    if (!scenario) return;
    if (!runnableCapabilities.has(scenario.capability)) return;
    const routeChanged = scenarioId !== null && scenarioId !== scenario.id;
    if (routeChanged && !options?.preserveInput) {
      // Returning to the route picker and choosing another route must not
      // carry a prompt, upload, estimate, or output into the new task.
      setPrompt("");
      setTextInput("");
      setTextAttachments([]);
      setPromptOutput("");
      setUploadPreview(null);
      setDraft(null);
    }
    setScenarioId(scenario.id);
    setResultType(scenario.resultType);
    setCapability(scenario.capability);
    setSelectedModel(null);
    if (scenario.inputKind !== "reference") {
      setUploadPreview(null);
    }
  }

  function selectScenarioByCapability(nextCapability: Capability, options?: { preserveInput?: boolean }) {
    if (!runnableCapabilities.has(nextCapability)) return;
    const preferred = scenarioDefinitions.find((item) => item.capability === nextCapability);
    if (preferred) selectScenario(preferred.id, options);
  }

  function selectResultType(nextType: ResultType) {
    const preferred = availableScenarios.find((scenario) => scenario.resultType === nextType)
      ?? scenarioDefinitions.find((scenario) => scenario.resultType === nextType);
    if (!preferred) return;
    const needsRouteChoice = nextType === "image" || nextType === "video" || nextType === "audio";
    if (resultType !== nextType || needsRouteChoice) {
      // Selecting a route-based type starts a clean route. Keep text history,
      // but do not leak a previous prompt, attachment, estimate, or result.
      setPrompt("");
      setTextInput("");
      setTextAttachments([]);
      setPromptOutput("");
      setUploadPreview(null);
      setDraft(null);
    }
    setResultType(nextType);
    // A type with multiple routes is a decision in its own right. Do not
    // outline the first route before the user chooses it; otherwise the
    // workspace says "choose a method" while one method already looks active.
    if (needsRouteChoice) {
      setScenarioId(null);
      setCapability(null);
      setStep("subtype");
    } else {
      // Text and prompt have one continuous workspace, so their first route
      // is an implementation detail rather than an extra user decision.
      setScenarioId(preferred.id);
      setCapability(preferred.capability);
      setStep("input");
    }
  }

  async function loadDraftFromLink(draftId: string) {
    setError(null);
    try {
      const linkedDraft = await fetchDraft(draftId);
      if (!allowedCapabilities.includes(linkedDraft.capability) || !runnableCapabilities.has(linkedDraft.capability)) {
        throw new Error("Этот формат больше недоступен.");
      }
      setDraft(linkedDraft);
      selectScenarioByCapability(linkedDraft.capability, { preserveInput: true });
      setPrompt(linkedDraft.prompt || "");
      setSelectedModel(null);
      if (typeof linkedDraft.params?.route_scenario === "string") {
        const routeScenario = linkedDraft.params.route_scenario as ScenarioId;
        const route = scenarioDefinitions.find((scenario) => scenario.id === routeScenario && scenario.capability === linkedDraft.capability);
        if (route) {
          selectScenario(route.id, { preserveInput: true });
        }
      }
      applyImageParams(linkedDraft.params || {});
      if (typeof linkedDraft.params?.target_capability === "string") {
        setPromptTargetCapability(linkedDraft.params.target_capability as Capability);
      }
      setScreen("create");
      setStep(linkedDraft.prompt ? "review" : "input");
      setStatus("Черновик загружен.");
      setTimeout(() => setStatus(null), 3000);
    } catch (err) {
      setError("Не удалось открыть черновик. Возможно, ссылка устарела. Вернитесь на главный экран или в бот.");
    }
  }

  async function loadTaskFromLink(taskId: string) {
    setError(null);
    try {
      const linkedTask = await fetchTask(taskId);
      setSelectedTask(linkedTask);
      setTasks((current) => {
        if (current.some((task) => task.id === linkedTask.id)) return current;
        return [linkedTask, ...current];
      });
      setScreen("gallery");
      setStatus("Открыли результат из ссылки.");
      setTimeout(() => setStatus(null), 3000);
    } catch {
      setScreen("gallery");
      setError("Не удалось открыть результат. Возможно, ссылка устарела или файл удалён.");
    }
  }

  async function loadWorkspaceData(options: { showError?: boolean } = {}) {
    const showError = options.showError !== false;
    if (showError) setError(null);
    setLoadingWorkspace(true);
    try {
      const [tasksResult, balanceResult, transactionsResult] = await Promise.allSettled([
        fetchTasks(),
        fetchBalance(),
        fetchTransactions()
      ]);
      // OpenRouter remains a private pricing anchor on the backend. The
      // active executor is AGY, so model catalog data never enters the app.
      setModels([]);
      let hasPartialFailure = false;
      if (tasksResult.status === "fulfilled") {
        setTasks(Array.isArray(tasksResult.value) ? tasksResult.value.filter(Boolean) : []);
      } else {
        hasPartialFailure = true;
        console.error("fetchTasks failed:", tasksResult.reason);
      }
      if (balanceResult.status === "fulfilled") {
        setBalance(balanceResult.value.token_balance ?? null);
      } else {
        hasPartialFailure = true;
        console.error("fetchBalance failed:", balanceResult.reason);
      }
      if (transactionsResult.status === "fulfilled") {
        setTransactions(Array.isArray(transactionsResult.value) ? transactionsResult.value.filter(Boolean) : []);
      } else {
        hasPartialFailure = true;
        console.error("fetchTransactions failed:", transactionsResult.reason);
      }
      if (hasPartialFailure && showError) {
        setError("Не удалось обновить часть данных. Проверьте интернет-соединение.");
      }
    } finally {
      setLoadingWorkspace(false);
    }
  }

  const providers = useMemo(() => ["all", ...Array.from(new Set(models.map((model) => model.provider)))], [models]);
  const visibleModels = useMemo(
    () => capability ? sortModels(filterModels(models, query, provider, capability), sort) : [],
    [models, query, provider, capability, sort]
  );
  const recommendedModels = useMemo(
    () => capability ? sortModels(filterModels(models, "", "all", capability), "recommended").slice(0, 5) : [],
    [models, capability]
  );

  function getPresetIdForScenario(scenId: ScenarioId): string | null {
    switch (scenId) {
      case "prompt.universal":
      case "prompt.image":
        return "universal_prompt";
      case "prompt.flux":
        return "flux_prompt";
      case "prompt.json_image":
        return "json_image";
      case "prompt.json_video":
      case "prompt.video":
        return "json_video";
      case "prompt.research":
        return "deep_research";
      default:
        return null;
    }
  }

  function currentParams() {
    const base: Record<string, unknown> = {};
    if (scenarioId) base.route_scenario = scenarioId;
    if (!capability) return base;
    if (capability === "text.prompt_builder") {
      const presetId = scenarioId ? getPresetIdForScenario(scenarioId) : null;
      return {
        ...base,
        target_capability: promptTargetCapability,
        preset_id: presetId,
        prompt_format: (scenarioId === "prompt.json_image" || scenarioId === "prompt.json_video") ? "json" : "plain"
      };
    }
    if (capability.startsWith("text.")) {
      return {
        ...base,
        text_route: scenarioId,
        tone: textTone,
        system_prompt: systemPromptForTone(textTone, scenarioId ?? undefined)
      };
    }
    if (capability.startsWith("image.")) {
      base.aspect_ratio = aspectRatio;
      if (seed) {
        base.seed = Number(seed);
      }
      if (negativePrompt) {
        base.negative_prompt = negativePrompt;
      }
      base.n = nVariants;
      base.quality = imageQuality;
    }
    return base;
  }

  async function prepareDraftForCurrentInput(promptOverride = prompt, capabilityOverride = capability) {
    if (!capabilityOverride) {
      throw new Error("Сначала выберите тип контента.");
    }
    if (!runnableCapabilities.has(capabilityOverride)) {
      throw new Error("Этот режим пока недоступен.");
    }
    const updatePayload = {
      prompt: promptOverride,
      selected_model_id: resolvedModelId,
      model_selection_mode: modelSelectionMode === "manual" ? ("manual" as const) : ("auto" as const),
      params: currentParams()
    };
    if (draft && draft.capability === capabilityOverride) {
      return updateDraft(draft.id, updatePayload);
    }
    return createDraft({
      source_interface: runtime,
      capability: capabilityOverride,
      prompt: promptOverride,
      selected_model_id: resolvedModelId,
      params: currentParams()
    });
  }

  async function refreshEstimate(silent = false) {
    if (!isAuthenticated) return;
    setEstimating(true);
    setError(null);
    try {
      const activeDraft = await prepareDraftForCurrentInput();
      setDraft(activeDraft);
      const estimate = await estimateDraft(activeDraft.id);
      setDraft(estimate.draft);
      tg?.HapticFeedback?.impactOccurred?.("light");
    } catch (err) {
      if (!silent) {
        setError(err instanceof Error ? err.message : "Не удалось рассчитать стоимость.");
      }
    } finally {
      setEstimating(false);
    }
  }

  async function handleCreateDraft() {
    await refreshEstimate(false);
  }

  async function waitForTask(task: Task): Promise<Task> {
    if (["completed", "failed", "expired", "cancelled", "refunded"].includes(task.status)) return task;
    let current = task;
    // A second tab can receive the existing processing task. Polling makes
    // that path behave like the original submit instead of showing a stale
    // spinner forever. The backend scheduler remains the recovery authority.
    let transientFailures = 0;
    for (let attempt = 0; attempt < 150; attempt += 1) {
      await new Promise((resolve) => window.setTimeout(resolve, 2000));
      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        setStatus("Нет соединения. Работа продолжается, ждём сеть…");
        await new Promise((resolve) => {
          const resume = () => {
            window.removeEventListener("online", resume);
            resolve(undefined);
          };
          window.addEventListener("online", resume, { once: true });
        });
      }
      try {
        current = await fetchTask(task.id);
        transientFailures = 0;
        setTasks((items) => items.map((item) => item.id === current.id ? current : item));
        if (["completed", "failed", "expired", "cancelled", "refunded"].includes(current.status)) return current;
      } catch (error) {
        if (!(error instanceof ApiError) || !error.retryable) throw error;
        transientFailures += 1;
        if (transientFailures >= 5) {
          setStatus("Связь временно прервалась. Работа продолжается — откройте галерею чуть позже.");
          return current;
        }
        await new Promise((resolve) => window.setTimeout(resolve, Math.min(8_000, 1000 * 2 ** transientFailures)));
      }
    }
    return current;
  }

  async function handleGenerate() {
    if (!isAuthenticated) return;
    if (!capability) {
      setError("Сначала выберите тип контента.");
      setStep("type");
      return;
    }
    setBusy(true);
    setGenerationStartedAt(Date.now());
    setGenerationElapsedSeconds(0);
    setGenerationPhase("preparing");
    setError(null);
    setStatus("Подготавливаем задачу…");
    try {
      if (capability === "image.image_to_image" && !uploadPreview) {
        throw new Error("Загрузите изображение-образец перед запуском.");
      }
      let activeDraft = await prepareDraftForCurrentInput();
      if (!activeDraft.estimated_cost) {
        setGenerationPhase("preparing");
        const estimate = await estimateDraft(activeDraft.id);
        activeDraft = estimate.draft;
      }
      setDraft(activeDraft);
      setGenerationPhase("sending");
      setStatus("Передаём задачу в сервис…");
      // Give the hand-off state one paint before the long submit/poll request;
      // after that the provider may spend several minutes processing it.
      await new Promise((resolve) => window.setTimeout(resolve, 120));
      setGenerationPhase("processing");
      setStatus("Создаём изображение…");
      const task = await waitForTask(await generateDraft(activeDraft.id));
      setGenerationPhase("saving");
      setStatus("Проверяем результат…");
      tg?.HapticFeedback?.notificationOccurred?.(task.status === "failed" ? "error" : "success");
      await loadWorkspaceData({ showError: false });
      setScreen("gallery");
      // Keep the gallery list visible after launch. Opening a full-screen
      // result dialog automatically can trap mobile taps behind its backdrop
      // while AGY is still processing the task. Add the returned task
      // optimistically when the list endpoint has not returned it yet.
      setSelectedTask(null);
      setTasks((current) => current.some((item) => item.id === task.id) ? current : [task, ...current].slice(0, 24));
      if (task.status === "failed" || task.status === "cancelled" || task.status === "refunded") {
        // Keep the user in the same route so retrying cannot silently switch
        // to another content type or lose the chosen parameters.
        setSelectedTask(null);
        setScreen("create");
        setStep("review");
        setStatus(null);
        setGenerationPhase("error");
        setError(task.error_message || "Не удалось создать изображение. Проверьте описание и попробуйте ещё раз.");
      } else {
        setGenerationPhase("idle");
        setStatus(task.status === "completed" ? "Изображение готово." : "Создание началось. Результат появится в галерее.");
        setTimeout(() => setStatus(null), 4000);
      }
    } catch (err) {
      setStatus(null);
      setScreen("create");
      setStep("review");
      setGenerationPhase("error");
      setError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Не удалось начать создание. Попробуйте ещё раз.");
    } finally {
      setBusy(false);
      setGenerationStartedAt(null);
    }
  }

  async function handleTextSend() {
    const nextMessage = textInput.trim();
    if (!nextMessage || !isAuthenticated || busy) return;
    const userTurn: TextTurn = {
      id: `turn-${Date.now()}-user`,
      role: "user",
      content: nextMessage,
      createdAt: new Date().toISOString(),
      modelId: resolvedModelId,
      attachments: textAttachments.length > 0 ? [...textAttachments] : undefined
    };
    setBusy(true);
    setError(null);
    setStatus("Готовим ответ...");
    try {
      const boundedPrompt = buildBoundedTextPrompt(textTurns, nextMessage, activeTextConversation.summary);
      const activeDraft = await prepareDraftForCurrentInput(boundedPrompt, "text.chat");
      const estimate = await estimateDraft(activeDraft.id);
      setDraft(estimate.draft);
      const task = await waitForTask(await generateDraft(activeDraft.id));
      const completedTask = task.assets?.length ? task : await fetchTask(task.id);
      const assistantContent = sanitizeTextPayload(
        completedTask.assets?.find((asset) => asset.metadata?.text)?.metadata?.text
      );
      const nextTurn: TextTurn = assistantContent
        ? {
            id: `turn-${Date.now()}-assistant`,
            role: "assistant",
            content: assistantContent,
            createdAt: new Date().toISOString(),
            modelId: completedTask.model_id
          }
        : {
            id: `turn-${Date.now()}-error`,
            role: "error",
            content: completedTask.error_message || "Не удалось получить ответ. Попробуйте ещё раз.",
            createdAt: new Date().toISOString(),
            modelId: completedTask.model_id
          };
      setTextConversations((current) => current.map((conversation) => {
        if (conversation.id !== activeTextConversation.id) return conversation;
        const allTurns = [...conversation.turns, userTurn, nextTurn];
        const retainedTurns = allTurns.slice(-40);
        return {
          ...conversation,
          title: conversation.turns.length === 0 ? nextMessage.slice(0, 56) : conversation.title,
          summary: buildConversationSummary(conversation.summary, allTurns.slice(0, -40)),
          turns: retainedTurns,
          updatedAt: new Date().toISOString()
        };
      }));
      setTextInput("");
      setPrompt("");
      setTextAttachments([]);
      setUploadPreview(null);
      setStatus(nextTurn.role === "assistant" ? "Ответ готов." : nextTurn.content);
      setTimeout(() => setStatus(null), 3500);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Не удалось получить ответ. Попробуйте ещё раз.");
    } finally {
      setBusy(false);
    }
  }

  function handleNewTextConversation() {
    const conversation = makeTextConversation();
    setTextConversations((current) => [conversation, ...current].slice(0, 12));
    setActiveTextConversationId(conversation.id);
    setTextInput("");
    setPrompt("");
    setTextAttachments([]);
    setUploadPreview(null);
    setStatus("Новый диалог готов.");
    setTimeout(() => setStatus(null), 2500);
  }

  async function handlePromptGenerate() {
    if (!isAuthenticated || promptBusy || prompt.trim().length < 3) return;
    setPromptBusy(true);
    setError(null);
    setStatus("Создаём промпт...");
    try {
      const result = await promptBuilderGenerate(prompt, promptTargetCapability);
      const promptTask = await waitForTask(result.task);
      const output = sanitizeTextPayload(
        promptTask.assets?.find((asset) => asset.metadata?.text)?.metadata?.text
      );
      if (promptTask.status === "failed") {
        throw new Error(promptTask.error_message || "Не удалось создать промпт. Измените описание и попробуйте ещё раз.");
      }
      if (!output) throw new Error("Не удалось создать промпт. Измените описание и попробуйте ещё раз.");
      setPromptOutput(output);
      void loadWorkspaceData();
      setStatus("Промпт готов.");
      setTimeout(() => setStatus(null), 2500);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Не удалось создать промпт. Попробуйте ещё раз.");
    } finally {
      setPromptBusy(false);
    }
  }

  useEffect(() => {
    if (!isAuthenticated || busy) return;
    if (!capability) return;
    if (!runnableCapabilities.has(capability)) return;
    if (prompt.trim().length < 3) return;
    if (capability === "image.image_to_image" && !uploadPreview) return;
    const timer = window.setTimeout(() => {
      void refreshEstimate(true);
    }, 850);
    return () => window.clearTimeout(timer);
  }, [
    isAuthenticated,
    busy,
    capability,
    scenarioId,
    prompt,
    selectedModel,
    aspectRatio,
    textTone,
    promptTargetCapability,
    uploadPreview,
    runnableCapabilities
  ]);

  async function handleUpload(file: File) {
    if (!isAuthenticated) return;
    if (!capability || !resultType) {
      setError("Сначала выберите тип контента.");
      return;
    }
    setError(null);
    const maxSize = 20 * 1024 * 1024;
    if (file.size > maxSize) {
      setError("Файл слишком большой. Максимальный размер — 20 МБ.");
      return;
    }
    const isTextAttachment = resultType === "text";
    const isAllowed = isTextAttachment
      ? file.type.startsWith("image/") || file.type === "application/pdf" || file.type === "text/plain" || file.type === "text/markdown" || file.type === "text/csv" || file.type === "application/json" || file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      : file.type.startsWith("image/");
    if (!isAllowed) {
      setError(isTextAttachment ? "Поддерживаются изображения, PDF и текстовые документы." : "Загрузите изображение в формате JPG, PNG или WebP.");
      return;
    }
    if (file.type.startsWith("image/")) {
      setUploadPreview(URL.createObjectURL(file));
    }
    try {
      const activeDraft = await prepareDraftForCurrentInput();
      setDraft(activeDraft);
      await uploadAttachment(activeDraft.id, file, runtime === "telegram_mini_app" ? "telegram_mini_app" : "standalone_web");
      if (resultType === "text") {
        setTextAttachments((current) => [...current, { name: file.name, mimeType: file.type || "application/octet-stream", size: file.size }]);
      }
      setStatus(resultType === "text" ? "Файл прикреплён к диалогу." : "Изображение прикреплено как образец.");
      setTimeout(() => setStatus(null), 3000);
    } catch (err) {
      setError("Не удалось прикрепить файл. Попробуйте ещё раз.");
    }
  }

  async function handleSelectModel(modelId: string) {
    setError(null);
    setSelectedModel(modelId);
    setModelSelectionMode("manual");
    try {
      if (draft && isAuthenticated) {
        const updated = await updateDraft(draft.id, {
          selected_model_id: modelId,
          model_selection_mode: "manual"
        });
        setDraft(updated);
      }
      setShowModelOverlay(false);
      if (screen === "models") {
        setScreen("create");
      }
      setStatus(`Модель выбрана: ${getPublicModelLabel(capability, modelId, null, IMAGE_MODEL_LABEL, generationUsesAgy)}`);
      setTimeout(() => setStatus(null), 3000);
    } catch (err) {
      setError("Не удалось сохранить выбор модели.");
    }
  }

  async function handleUseAutoModel() {
    setError(null);
    setSelectedModel(null);
    setModelSelectionMode("auto");
    try {
      if (draft && isAuthenticated) {
        const updated = await updateDraft(draft.id, {
          selected_model_id: null,
          model_selection_mode: "auto"
        });
        setDraft(updated);
      }
      setShowModelOverlay(false);
      if (screen === "models") {
        setScreen("create");
      }
      setStatus("Автоматический подбор модели включён.");
      setTimeout(() => setStatus(null), 3000);
    } catch {
      setError("Не удалось включить автоподбор.");
    }
  }

  async function handleDeleteTask(taskId: string) {
    if (!isAuthenticated) return;
    setError(null);
    try {
      await deleteTask(taskId);
      setTasks((current) => current.filter((t) => t.id !== taskId));
      setStatus("Результат удалён из галереи.");
      setTimeout(() => setStatus(null), 3000);
    } catch (err) {
      setError("Не удалось удалить результат. Попробуйте ещё раз.");
    }
  }

  async function handleUseTaskAsReference(task: Task) {
    if (!isAuthenticated) return;
    if (!runnableCapabilities.has("image.image_to_image")) {
      setError("Создание по образцу пока недоступно.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const reference = await createReferenceDraftFromTask(task.id);
      setDraft(reference.draft);
      // The source description is already represented by the reference
      // image. Keep the change field empty so the user does not accidentally
      // submit the old prompt as if it were an instruction to edit.
      setPrompt("");
      setSelectedModel(null);
      selectScenario("image.image_to_image");
      applyImageParams(reference.draft.params || task.params || {});
      setUploadPreview(apiUrl(reference.preview_url) || null);
      setScreen("create");
      setStep("input");
      setStatus("Образец добавлен. Укажите, что изменить.");
      setTimeout(() => setStatus(null), 3000);
    } catch {
      setError("Не удалось использовать этот результат как образец.");
    } finally {
      setBusy(false);
    }
  }

  function applyImageParams(params: Record<string, unknown>) {
    setAspectRatio("1:1");
    setImageQuality("standard");
    setNVariants(1);
    setNegativePrompt("");
    if (typeof params.aspect_ratio === "string" && aspectRatios.includes(params.aspect_ratio)) {
      setAspectRatio(params.aspect_ratio);
    }
    if (typeof params.quality === "string" && ["standard", "hd"].includes(params.quality)) {
      setImageQuality(params.quality);
    }
    if (typeof params.n === "number" && [1, 2, 4].includes(params.n)) {
      setNVariants(params.n);
    }
    if (typeof params.negative_prompt === "string") {
      setNegativePrompt(params.negative_prompt);
    }
  }

  async function handleReuseTask(task: Task) {
    const capabilityValue = task.capability as Capability;
    if (capabilityValue === "image.image_to_image") {
      await handleUseTaskAsReference(task);
      return;
    }

    setDraft(null);
    setPrompt(task.prompt || "");
    setSelectedModel(null);
    setUploadPreview(null);
    applyImageParams(task.params || {});

    if (!allowedCapabilities.includes(capabilityValue)) {
      setResultType(null);
      setScenarioId(null);
      setCapability(null);
      setStep("type");
      setScreen("create");
      setStatus("У этой работы не указан тип. Выберите новый формат, чтобы продолжить.");
      setTimeout(() => setStatus(null), 3500);
      return;
    }

    if (!runnableCapabilities.has(capabilityValue)) {
      setResultType(null);
      setScenarioId(null);
      setCapability(null);
      setStep("type");
      setScreen("create");
      setStatus("Этот формат пока недоступен. Выберите другой вариант.");
      setTimeout(() => setStatus(null), 3500);
      return;
    }

    const routeScenario = typeof task.params?.route_scenario === "string"
      ? task.params.route_scenario as ScenarioId
      : null;
    const route = routeScenario
      ? scenarioDefinitions.find((scenario) => scenario.id === routeScenario && scenario.capability === capabilityValue)
      : null;
    if (route) {
      selectScenario(route.id, { preserveInput: true });
    } else if (allowedCapabilities.includes(capabilityValue)) {
      selectScenarioByCapability(capabilityValue, { preserveInput: true });
    }

    if (capabilityValue === "text.chat") {
      setTextInput(task.prompt || "");
      setTextAttachments([]);
      setStep("input");
    } else if (capabilityValue === "text.prompt_builder") {
      const output = sanitizeTextPayload(task.assets?.find((asset) => asset.metadata?.text)?.metadata?.text);
      setPromptOutput(output);
      setStep("input");
    } else {
      setStep("review");
    }
    setScreen("create");
    setStatus("Черновик восстановлен. Проверьте описание и настройки.");
    setTimeout(() => setStatus(null), 3500);
  }

  async function handleResultAction(task: Task, action: string) {
    if (!isAuthenticated) return;
    setBusy(true);
    setError(null);
    try {
      const result = await executeResultAction(task.id, action);
      if (!result.draft) {
        throw new Error("Не удалось подготовить продолжение работы. Попробуйте открыть результат ещё раз.");
      }
      if (!allowedCapabilities.includes(result.draft.capability) || !runnableCapabilities.has(result.draft.capability)) {
        throw new Error("Формат этого результата пока недоступен для продолжения.");
      }
      setDraft(result.draft);
      setPrompt(result.draft.prompt || "");
      setSelectedModel(null);
      selectScenarioByCapability(result.draft.capability, { preserveInput: true });
      setUploadPreview(apiUrl(result.preview_url) || null);
      setSelectedTask(null);
      setScreen("create");
      setStep("review");
      setStatus(action === "use_as_prompt" ? "Промпт перенесён в черновик изображения." : "Создали черновик для продолжения.");
      setTimeout(() => setStatus(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Не удалось продолжить работу с результатом.");
    } finally {
      setBusy(false);
    }
  }

  function handleScreenChange(nextScreen: Screen) {
    // A result detail is a modal owned by the gallery. Clear it before moving
    // between primary sections so it can never leave a backdrop intercepting
    // taps after navigation.
    setSelectedTask(null);
    setShowModelOverlay(false);
    if (nextScreen !== screen) {
      setError(null);
      setStatus(null);
    }
    setScreen(nextScreen);
  }

  return (
    <div className="app-shell" data-runtime={runtime}>
      <aside className="sidebar">
        <Brand />
        <DesktopNav screen={screen} onChange={handleScreenChange} />
      </aside>
      <main className="main">
        <TopBar runtime={runtime} balance={balance} authStatus={authStatus} />
        {!isAuthenticated && <AuthBanner runtime={runtime} authStatus={authStatus} />}
        {error && <Notice tone="error" text={error} />}
        {status && <Notice tone="info" text={status} />}

        <ScreenErrorBoundary key={screen} onReset={() => handleScreenChange("create")}>
        {screen === "create" && (
          <CreateScreen
            step={step}
            setStep={setStep}
            resultTypes={resultTypeDefinitions}
            availableResultTypes={availableResultTypes.map((type) => type.id)}
            resultType={resultType}
            setResultType={selectResultType}
            scenarios={activeTypeScenarios}
            scenario={activeScenario}
            selectScenario={selectScenario}
            runnableCapabilities={runnableCapabilities}
            capability={capability}
            prompt={prompt}
            setPrompt={setPrompt}
            aspectRatio={aspectRatio}
            setAspectRatio={setAspectRatio}
            textTone={textTone}
            setTextTone={setTextTone}
            promptTargetCapability={promptTargetCapability}
            setPromptTargetCapability={setPromptTargetCapability}
            currentModel={currentModel}
            recommendedModels={recommendedModels}
            uploadPreview={uploadPreview}
            busy={busy}
            generationPhase={generationPhase}
            generationElapsedSeconds={generationElapsedSeconds}
            estimating={estimating}
            isAuthenticated={isAuthenticated}
            estimatedCost={draft?.estimated_cost}
            generationError={error}
            onDraft={handleCreateDraft}
            onGenerate={handleGenerate}
            onUpload={handleUpload}
            onSelectModel={handleSelectModel}
            onUseAutoModel={handleUseAutoModel}
            openModels={() => setShowModelOverlay(true)}
            deepLinkAction={deepLinkAction}
            hasLinkedDraft={Boolean(draft && new URLSearchParams(window.location.search).get("draft_id"))}
            runtime={runtime}
            modelSelectionMode={modelSelectionMode}
            setModelSelectionMode={setModelSelectionMode}
            showModelOverlay={showModelOverlay}
            setShowModelOverlay={setShowModelOverlay}
            seed={seed}
            setSeed={setSeed}
            negativePrompt={negativePrompt}
            setNegativePrompt={setNegativePrompt}
            nVariants={nVariants}
            setNVariants={setNVariants}
            imageQuality={imageQuality}
            setImageQuality={setImageQuality}
            visibleModels={visibleModels}
            models={models}
            providers={providers}
            query={query}
            setQuery={setQuery}
            provider={provider}
            setProvider={setProvider}
            sort={sort}
            setSort={setSort}
            textInput={textInput}
            setTextInput={(value) => {
              setTextInput(value);
              setPrompt(value);
            }}
            textTurns={textTurns}
            textConversations={textConversations}
            activeTextConversationId={activeTextConversation.id}
            setActiveTextConversationId={setActiveTextConversationId}
            textAttachments={textAttachments}
            setTextAttachments={setTextAttachments}
            onTextSend={handleTextSend}
            onNewTextConversation={handleNewTextConversation}
            promptOutput={promptOutput}
            setPromptOutput={setPromptOutput}
            onPromptGenerate={handlePromptGenerate}
            promptBusy={promptBusy}
          />
        )}
        
        {screen === "gallery" && (
          <GalleryScreen
            tasks={tasks}
            selectedTask={selectedTask}
            isAuthenticated={isAuthenticated}
            loading={loadingWorkspace}
            onCreate={() => handleScreenChange("create")}
            onOpen={setSelectedTask}
            onCloseDetail={() => setSelectedTask(null)}
            onDelete={handleDeleteTask}
            onReuse={handleReuseTask}
            onUseAsReference={handleUseTaskAsReference}
            onAction={handleResultAction}
          />
        )}
        
        {screen === "models" && (
          <ModelCatalog
            models={models}
            isAuthenticated={isAuthenticated}
            loading={loadingWorkspace}
            providers={providers}
            query={query}
            setQuery={setQuery}
            provider={provider}
            setProvider={setProvider}
            sort={sort}
            setSort={setSort}
            runnableCapabilities={runnableCapabilities}
            selectedModel={selectedModel}
            onSelect={handleSelectModel}
            onUseAuto={handleUseAutoModel}
            onResetFilters={() => {
              setQuery("");
              setProvider("all");
              setSort("recommended");
            }}
          />
        )}
        
        {screen === "balance" && <BalanceScreen balance={balance} transactions={transactions} loading={loadingWorkspace} />}
        {screen === "settings" && <SettingsScreen runtime={runtime} authStatus={authStatus} profile={telegramProfile} />}
        </ScreenErrorBoundary>
      </main>
      <MobileNav screen={screen} onChange={handleScreenChange} />
    </div>
  );
}

function Brand() {
  return (
    <div className="brand">
      <div className="brand-mark">Z</div>
      <div>
        <strong>Znich</strong>
        <span>Студия контента</span>
      </div>
    </div>
  );
}

function TopBar({ runtime, balance, authStatus }: { runtime: string; balance: string | null; authStatus: AuthStatus }) {
  const authenticated = authStatus === "authenticated";
  return (
    <header className="topbar compact-topbar">
      <div className="topbar-intro">
        <div className="topbar-title-line">
          <h1>Znich</h1>
          <span>Создание контента</span>
        </div>
      </div>
      <div className="top-actions compact-top-actions">
        <span className="topbar-icon-status balance-status" title={balance === null ? "Баланс недоступен" : `Баланс: ${formatTokens(balance, { exact: true })}`} aria-label={balance === null ? "Баланс недоступен" : `Баланс ${formatTokens(balance, { exact: true })}`}>
          <Coins size={15} />
          {balance !== null && <strong>{formatTokens(balance, { exact: true }).replace(/\s+токен(?:а|ов)?$/, "")}</strong>}
        </span>
        <span className={`topbar-icon-status ${authenticated ? "ok" : ""}`} title={authenticated ? "Подключено" : authStatus === "checking" ? "Проверяем вход" : "Режим просмотра"} aria-label={authenticated ? "Подключено" : authStatus === "checking" ? "Проверяем вход" : "Режим просмотра"}>
          {authenticated ? <CircleCheck size={16} /> : <Bot size={16} />}
        </span>
      </div>
    </header>
  );
}

function GenerationProgressCard({ phase, elapsedSeconds }: { phase: GenerationPhase; elapsedSeconds: number }) {
  const stages: Array<{ id: Exclude<GenerationPhase, "idle" | "error">; label: string }> = [
    { id: "preparing", label: "Подготовка" },
    { id: "sending", label: "Передача" },
    { id: "processing", label: "Создание" },
    { id: "saving", label: "Сохранение" }
  ];
  const currentIndex = Math.max(0, stages.findIndex((stage) => stage.id === phase));
  const phaseCopy: Record<GenerationPhase, string> = {
    idle: "Почти готово",
    preparing: "Проверяем описание и настройки…",
    sending: "Передаём запрос в сервис генерации…",
    processing: "Сервис создаёт изображение. Это может занять до нескольких минут…",
    saving: "Получаем файл и сохраняем его в галерее…",
    error: "Не удалось завершить задачу."
  };
  return (
    <section className="generation-progress-card" role="status" aria-live="polite" aria-busy="true" aria-label="Создание изображения">
      <div className="generation-progress-orb" aria-hidden="true">
        <span className="generation-progress-orb-ring" />
        <Loader2 className="spin" size={24} />
      </div>
      <div className="generation-progress-copy">
        <span className="eyebrow">Идёт создание</span>
        <h3>Создаём изображение</h3>
        <p>{phaseCopy[phase]}</p>
        <div className="generation-progress-track" aria-hidden="true"><span /></div>
        <div className="generation-progress-stages">
          {stages.map((stage, index) => (
            <span key={stage.id} className={index <= currentIndex ? "active" : ""}>
              <i aria-hidden="true">{index < currentIndex ? "✓" : index + 1}</i>{stage.label}
            </span>
          ))}
        </div>
        <small>{elapsedSeconds > 0 ? `Прошло ${elapsedSeconds} с` : "Окно можно оставить открытым — результат появится здесь."}</small>
      </div>
    </section>
  );
}

function CreateScreen(props: {
  step: WizardStep;
  setStep: (value: WizardStep) => void;
  resultTypes: ResultTypeDefinition[];
  availableResultTypes: ResultType[];
  resultType: ResultType | null;
  setResultType: (value: ResultType) => void;
  scenarios: CreationScenario[];
  scenario: CreationScenario | null;
  selectScenario: (value: ScenarioId) => void;
  runnableCapabilities: Set<Capability>;
  capability: Capability | null;
  prompt: string;
  setPrompt: (value: string) => void;
  aspectRatio: string;
  setAspectRatio: (value: string) => void;
  textTone: string;
  setTextTone: (value: string) => void;
  promptTargetCapability: Capability;
  setPromptTargetCapability: (value: Capability) => void;
  currentModel: ModelItem | null;
  recommendedModels: ModelItem[];
  uploadPreview: string | null;
  busy: boolean;
  generationPhase: GenerationPhase;
  generationElapsedSeconds: number;
  estimating: boolean;
  isAuthenticated: boolean;
  estimatedCost: string | null | undefined;
  generationError: string | null;
  onDraft: () => void;
  onGenerate: () => void;
  onUpload: (file: File) => void;
  onSelectModel: (modelId: string) => void;
  onUseAutoModel: () => void;
  openModels: () => void;
  deepLinkAction: DeepLinkAction;
  hasLinkedDraft: boolean;
  runtime: string;

  modelSelectionMode: "auto" | "trend" | "cheap" | "fast" | "quality" | "manual";
  setModelSelectionMode: (value: "auto" | "trend" | "cheap" | "fast" | "quality" | "manual") => void;
  showModelOverlay: boolean;
  setShowModelOverlay: (value: boolean) => void;

  seed: string;
  setSeed: (value: string) => void;
  negativePrompt: string;
  setNegativePrompt: (value: string) => void;
  nVariants: number;
  setNVariants: (value: number) => void;
  imageQuality: string;
  setImageQuality: (value: string) => void;

  visibleModels: ModelItem[];
  models: ModelItem[];
  providers: string[];
  query: string;
  setQuery: (value: string) => void;
  provider: string;
  setProvider: (value: string) => void;
  sort: string;
  setSort: (value: string) => void;
  textInput: string;
  setTextInput: (value: string) => void;
  textTurns: TextTurn[];
  textConversations: TextConversation[];
  activeTextConversationId: string;
  setActiveTextConversationId: (value: string) => void;
  textAttachments: TextAttachment[];
  setTextAttachments: (value: TextAttachment[]) => void;
  onTextSend: () => void;
  onNewTextConversation: () => void;
  promptOutput: string;
  setPromptOutput: (value: string) => void;
  onPromptGenerate: () => void;
  promptBusy: boolean;
}) {
  const isRunnable = Boolean(props.capability && props.runnableCapabilities.has(props.capability));
  const hasMinPrompt = props.prompt.trim().length >= 3;
  const needsReference = props.scenario?.inputKind === "reference";
  const inputReady = hasMinPrompt && (!needsReference || Boolean(props.uploadPreview));
  const canGenerate = props.isAuthenticated && isRunnable && inputReady;
  // A failed estimate must not trap the user on the review screen. The
  // backend repeats the estimate before generation, so the primary action
  // can safely retry while still staying disabled during an active request.
  const canLaunch = canGenerate && !props.estimating;
  const modeTitle = props.scenario?.title ?? "";

  const readiness = !props.isAuthenticated
    ? "Откройте в Telegram"
    : !props.resultType
      ? "Выберите формат"
      : props.step === "type"
      ? "Формат выбран"
    : props.resultType === "text"
      ? "Чат готов"
      : props.resultType === "prompt"
        ? hasMinPrompt ? "Можно создавать" : "Добавьте идею"
        : props.resultType === "audio" && !isRunnable
          ? "Скоро"
          : props.resultType === "audio" && needsReference && !props.uploadPreview
            ? "Добавьте аудиофайл"
            : props.resultType === "audio" && !hasMinPrompt
              ? "Добавьте текст"
              : props.scenarios.length === 0
                ? "Нет доступных вариантов"
                : props.step === "subtype"
                  ? "Выберите способ"
                  : props.step === "params"
                    ? props.estimating
                      ? "Считаем стоимость"
                      : props.estimatedCost
                        ? "Можно создавать"
                        : "Проверьте настройки"
                  : props.step === "input" && needsReference && !props.uploadPreview
                    ? "Добавьте образец"
                  : props.step === "input" && !hasMinPrompt
                      ? "Добавьте описание"
                      : props.estimating
                        ? "Считаем стоимость"
                        : props.estimatedCost
                          ? "Можно создавать"
                          : "Заполните обязательные поля";

  const placeholder = props.scenario ? placeholderForScenario(props.scenario.id) : "";
  const supportedRatios = props.currentModel?.supported_aspect_ratios?.length
    ? props.currentModel.supported_aspect_ratios
    : aspectRatios;
  const supportedParameters = (props.currentModel?.supported_parameters?.length
    ? props.currentModel.supported_parameters
    : props.capability?.startsWith("image.")
      ? ["aspect_ratio", "quality", "n", "negative_prompt"]
      : props.capability === "text.prompt_builder"
        ? ["target_capability", "prompt_format"]
        : ["tone", "system_prompt"]
  ).filter((parameter) => parameter !== "seed");

  const stepsList: Array<[WizardStep, string]> = !props.resultType
    ? [["type", "Тип"], ["subtype", "Сценарий"], ["input", "Ввод"], ["params", "Настройки"], ["review", "Запуск"]]
    : props.resultType === "text"
    ? [["type", "Тип"], ["input", "Диалог"]]
    : props.resultType === "prompt"
      // Editing and the generated result stay visible together so the user
      // can refine the idea without a dead, unreachable progress step.
      ? [["type", "Тип"], ["input", "Идея"]]
      : props.resultType === "audio"
        ? [["type", "Тип"], ["subtype", "Задача"]]
        : [["type", "Тип"], ["subtype", "Режим"], ["input", "Описание"], ["params", "Настройки"], ["review", "Создание"]];

  const stageHeading = !props.resultType
    ? "Что хотите создать?"
    : props.step === "type"
      ? `Выбран формат: ${getResultTypeTitle(props.resultType)}`
      : props.resultType === "text"
        ? "Работа с текстом"
        : props.resultType === "prompt"
          ? "Генератор промптов"
          : props.resultType === "audio"
            ? "Работа со звуком"
            : props.step === "subtype"
              ? "Выберите способ"
              : props.step === "input"
                ? needsReference ? "Изменения в образце" : "Детали изображения"
                : props.step === "params"
                  ? "Настройте результат"
                  : "Проверьте запуск";
  const stageDescription = !props.resultType
    ? "Выберите формат — дальше покажем только нужные шаги и настройки."
    : props.step === "type"
      ? "Нажмите другую карточку, чтобы сменить формат."
      : props.resultType === "text"
        ? "Пишите и уточняйте в одном диалоге."
        : props.resultType === "prompt"
          ? "Опишите цель — получите готовый запрос."
          : props.resultType === "audio"
            ? "Выберите задачу и добавьте текст или файл."
            : props.step === "subtype"
              ? "Выберите, как получить изображение: с нуля или по образцу."
              : props.step === "input"
                ? needsReference ? "Образец уже выбран — добавьте короткое описание изменений." : "Добавьте сцену, объект и важные детали."
                : props.step === "params"
                  ? "Формат и дополнительные настройки влияют на результат."
                  : "Проверьте описание и стоимость перед созданием.";

  // Helper formatting for cost representation
  const formattedCost = useMemo(() => {
    if (!props.estimatedCost) return "Рассчитаем после ввода";
    const costNum = Number(props.estimatedCost);
    if (costNum < 1) {
      return `~${costNum.toFixed(2)} токена`;
    }
    return `~${Math.round(costNum).toLocaleString()} токенов`;
  }, [props.estimatedCost]);

  return (
    <section className="workspace">
    <section className={`creation-stage ${props.resultType ? `flow-${props.resultType}` : "flow-neutral"}`}>
        <div className="stage-glow" aria-hidden="true" />
        <ContentTypeRail
          types={props.resultTypes}
          availableTypes={props.availableResultTypes}
          activeType={props.resultType && props.availableResultTypes.includes(props.resultType) ? props.resultType : null}
          onSelect={props.setResultType}
        />
        <div className="route-steps" aria-label="Этапы создания" role="list">
          {stepsList.map(([s, label], index) => {
            const currentStepIdx = stepsList.findIndex(x => x[0] === props.step);
            const isPastOrCurrent = currentStepIdx >= index;
            return (
              <span
                key={s}
                role="listitem"
                aria-label={`Шаг ${index + 1}: ${label}${props.step === s ? ", текущий" : ""}`}
                aria-current={props.step === s ? "step" : undefined}
                className={`step-btn ${props.step === s ? "current active" : isPastOrCurrent ? "active" : ""}`}
              >
                <span className="step-number">{index + 1}</span>
                <span>{label}</span>
                {props.step === s && <ChevronRight size={12} aria-hidden="true" />}
              </span>
            );
          })}
        </div>
        <div className="stage-header">
          <div>
            <span className="eyebrow">Создание</span>
            <h2>{stageHeading}</h2>
            <p>{stageDescription}</p>
          </div>
          <div className={`stage-status ${canGenerate ? "ready" : ""}`}>
            <Sparkles size={16} />
            <span>{readiness}</span>
          </div>
        </div>

        {props.busy && props.resultType === "image" && (
          <GenerationProgressCard
            phase={props.generationPhase}
            elapsedSeconds={props.generationElapsedSeconds}
          />
        )}

        {props.hasLinkedDraft && (
          <div className="bot-context-card">
            <Bot size={18} />
            <div>
              <strong>Черновик из Telegram</strong>
              <span>Измените описание или настройки и продолжите создание.</span>
            </div>
          </div>
        )}

        {!props.resultType && props.step === "type" && (
          <div className="creation-welcome panel" role="status">
            <div className="creation-welcome-icon"><Sparkles size={20} /></div>
            <div>
              <strong>Сначала выберите формат</strong>
              <span>Изображения, тексты и промпты доступны сейчас. Видео и аудио появятся позже.</span>
            </div>
          </div>
        )}

        {props.resultType === "text" && props.step !== "type" && (
          <TextWorkspace
            turns={props.textTurns}
            conversations={props.textConversations}
            activeConversationId={props.activeTextConversationId}
            onConversationChange={props.setActiveTextConversationId}
            onNewConversation={props.onNewTextConversation}
            input={props.textInput}
            onInputChange={props.setTextInput}
            attachments={props.textAttachments}
            onUpload={props.onUpload}
            onRemoveAttachment={(attachment) => props.setTextAttachments(props.textAttachments.filter((item) => item !== attachment))}
            onSend={props.onTextSend}
            busy={props.busy}
            isAuthenticated={props.isAuthenticated}
            currentModel={props.currentModel}
            recommendedModels={props.recommendedModels}
            modelSelectionMode={props.modelSelectionMode}
            onSelectModel={props.onSelectModel}
            onUseAutoModel={props.onUseAutoModel}
            openModels={props.openModels}
            estimatedCost={props.estimatedCost}
            estimating={props.estimating}
          />
        )}

        {props.resultType === "prompt" && props.step !== "type" && (
          <PromptWorkspace
            prompt={props.prompt}
            setPrompt={props.setPrompt}
            output={props.promptOutput}
            setOutput={props.setPromptOutput}
            targetCapability={props.promptTargetCapability}
            setTargetCapability={props.setPromptTargetCapability}
            currentModel={props.currentModel}
            recommendedModels={props.recommendedModels}
            onSelectModel={props.onSelectModel}
            onUseAutoModel={props.onUseAutoModel}
            openModels={props.openModels}
            onGenerate={props.onPromptGenerate}
            busy={props.promptBusy}
            isAuthenticated={props.isAuthenticated}
          />
        )}

        {props.resultType === "audio" && props.scenario && props.step !== "type" && (
          <AudioWorkspace
            scenarios={props.scenarios}
            runnableCapabilities={props.runnableCapabilities}
            selectedScenario={props.scenario.id}
            prompt={props.prompt}
            setPrompt={props.setPrompt}
            uploadPreview={props.uploadPreview}
            onUpload={props.onUpload}
            isAuthenticated={props.isAuthenticated}
            onSelectScenario={(scenarioId) => {
              props.selectScenario(scenarioId);
              props.setStep("input");
            }}
          />
        )}

        {!generationUsesAgy && props.resultType !== "text" && props.resultType !== "prompt" && props.resultType !== "audio" && props.step === "model" && (
          <div className="model-step-layout">
            <div className="selected-model-banner panel">
              <span className="dock-label">Режим выбора модели</span>
              <div className="model-preset-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", margin: "12px 0" }}>
                <button
                  className={`preset-card ${props.modelSelectionMode === "auto" ? "active" : ""}`}
                  onClick={props.onUseAutoModel}
                >
                  <Sparkles size={16} />
                  <strong>Авто</strong>
                  <small>Баланс качества и цены</small>
                </button>
                <button
                  className={`preset-card ${props.modelSelectionMode === "trend" ? "active" : ""}`}
                  onClick={() => props.setModelSelectionMode("trend")}
                >
                  <Flame size={16} />
                  <strong>Популярная</strong>
                  <small>Выбор пользователей</small>
                </button>
                <button
                  className={`preset-card ${props.modelSelectionMode === "cheap" ? "active" : ""}`}
                  onClick={() => props.setModelSelectionMode("cheap")}
                >
                  <Coins size={16} />
                  <strong>Эконом</strong>
                  <small>Экономия баланса</small>
                </button>
                <button
                  className={`preset-card ${props.modelSelectionMode === "fast" ? "active" : ""}`}
                  onClick={() => props.setModelSelectionMode("fast")}
                >
                  <Zap size={16} />
                  <strong>Быстрая</strong>
                  <small>Быстрое создание</small>
                </button>
                <button
                  className={`preset-card ${props.modelSelectionMode === "quality" ? "active" : ""}`}
                  onClick={() => props.setModelSelectionMode("quality")}
                >
                  <Award size={16} />
                  <strong>Премиум</strong>
                  <small>Лучший результат</small>
                </button>
                <button
                  className={`preset-card ${props.modelSelectionMode === "manual" ? "active" : ""}`}
                  onClick={() => {
                    props.setModelSelectionMode("manual");
                    props.setShowModelOverlay(true);
                  }}
                >
                  <SlidersHorizontal size={16} />
                  <strong>Вручную</strong>
                  <small>{props.currentModel?.model_id ? (props.currentModel.model_id.split("/")[1] || props.currentModel.model_id) : "Выбрать из каталога"}</small>
                </button>
              </div>
              <div style={{ marginTop: "12px", borderTop: "1px solid var(--border)", paddingTop: "8px" }}>
                <span style={{ color: "var(--muted)", fontSize: "13px" }}>Выбранная модель: </span>
                <strong style={{ fontSize: "13px" }}>{props.currentModel ? (props.currentModel.display_name || props.currentModel.model_id) : "Подберём автоматически"}</strong>
                {props.currentModel && (
                  <span style={{ marginLeft: "12px", color: "var(--gold)", fontSize: "12px" }}>{formatTokens(props.currentModel.estimated_cost)}</span>
                )}
              </div>
            </div>

            {props.recommendedModels.length > 0 && (
              <div style={{ marginTop: "16px" }}>
                <span className="dock-label">Рекомендуемые модели</span>
                <div className="model-list" style={{ marginTop: "10px" }}>
                  {props.recommendedModels.map((model) => (
                    <article
                      key={model.model_id}
                      className={`model-row ${props.currentModel?.model_id === model.model_id ? "selected" : ""}`}
                    >
                      <div>
                        <strong>{getCatalogModelLabel(model)}</strong>
                        <span>{model.description || model.provider}</span>
                      </div>
                      <div className="model-meta">
                        <span>{formatTokens(model.estimated_cost)}</span>
                        <span>Качество: {Math.round(model.quality_score * 10)}/10</span>
                        {model.supported_parameters.slice(0, 2).map((p) => (
                          <span key={p}>{formatParameterName(p)}</span>
                        ))}
                      </div>
                      <button
                        className={props.currentModel?.model_id === model.model_id ? "primary" : "secondary"}
                        onClick={() => props.onSelectModel(model.model_id)}
                      >
                        {props.currentModel?.model_id === model.model_id ? <Check size={16} /> : "Выбрать"}
                      </button>
                    </article>
                  ))}
                </div>
              </div>
            )}

            <div className="wizard-navigation">
              <button className="secondary" onClick={() => props.setStep("type")}>« Назад</button>
              <button className="primary" onClick={() => props.setStep("subtype")}>Далее »</button>
            </div>
          </div>
        )}

        {props.resultType === "image" && props.step === "subtype" && (
          <div className="subtype-step-layout">
            <div className="scenario-grid" aria-label="Задача">
              {props.scenarios.map((scenario) => {
                const selectedScenarioId = props.scenario?.id;
                const isCompatible = props.modelSelectionMode === "manual" && props.currentModel
                  ? supportsCapability(props.currentModel, scenario.capability)
                  : true;

                let incompatibilityReason = "";
                if (props.modelSelectionMode === "manual" && props.currentModel && !isCompatible) {
                  const input = new Set(props.currentModel.input_modalities);
                  const output = new Set(props.currentModel.output_modalities);
                  if (scenario.inputKind === "reference" && !input.has("image")) {
                    incompatibilityReason = "Модель не принимает изображения";
                  } else if (scenario.capability.startsWith("text.") && !output.has("text")) {
                    incompatibilityReason = "Модель не создаёт текст";
                  } else if (scenario.capability.startsWith("image.") && !output.has("image")) {
                    incompatibilityReason = "Модель не создаёт изображения";
                  } else {
                    incompatibilityReason = "Эта модель не подходит для выбранной задачи";
                  }
                }

                return (
                  <button
                    key={scenario.id}
                    className={`scenario-card ${selectedScenarioId === scenario.id ? "active" : ""} ${!isCompatible ? "disabled" : ""}`}
                    disabled={!isCompatible}
                    onClick={() => {
                      if (!isCompatible) return;
                      props.selectScenario(scenario.id);
                      props.setStep("input");
                    }}
                    style={!isCompatible ? { opacity: 0.5, cursor: "not-allowed", pointerEvents: "auto" } : undefined}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", flexWrap: "wrap", gap: "6px" }}>
                      <span className="scenario-kind">{scenario.inputKind === "reference" ? "Образец + описание" : "Текстовое описание"}</span>
                      {incompatibilityReason && (
                        <span className="mismatch-badge" style={{ color: "var(--rose)", background: "rgba(244, 63, 94, 0.1)", padding: "2px 6px", borderRadius: "4px", fontSize: "11px", fontWeight: "bold" }}>
                          {incompatibilityReason}
                        </span>
                      )}
                    </div>
                    <strong>{scenario.title}</strong>
                    <small>{scenario.description}</small>
                  </button>
                );
              })}
            </div>

            <div className="wizard-navigation">
              <button className="secondary" onClick={() => props.setStep("type")}>« Назад</button>
            </div>
          </div>
        )}

        {props.resultType === "image" && props.scenario && props.step === "input" && (
          <div className="input-step-layout">
            <div className="draft-composer route-composer">
              <div className="prompt-surface" style={{ width: "100%" }}>
                <div className="prompt-orbit">
                  <span>{modeTitle}</span>
                </div>
                {needsReference && (
                  <UploadManager
                    onUpload={props.onUpload}
                    preview={props.uploadPreview}
                    emphasized={props.deepLinkAction === "upload"}
                    disabled={!props.isAuthenticated}
                  />
                )}
                <label>
                  <span>
                    {needsReference
                      ? "Что изменить в образце"
                      : "Сцена и детали"}
                  </span>
                  <textarea
                    value={props.prompt}
                    onChange={(e) => props.setPrompt(e.target.value)}
                    placeholder={placeholder}
                  />
                </label>
                <div className="prompt-footer">
                  <span>{props.prompt.trim().length} символов</span>
                </div>
                {props.deepLinkAction === "crop" && (
                  <div className="reference-note" style={{ marginTop: "12px" }}>
                    <strong>Обрезка кадра пока недоступна</strong>
                    <span>Изображение сохранится целиком. Укажите словами, что нужно изменить.</span>
                  </div>
                )}
              </div>
            </div>
            <div className="wizard-navigation">
              <button className="secondary" onClick={() => props.setStep("subtype")}>« Назад</button>
              <button 
                className="primary" 
                onClick={() => props.setStep("params")} 
                disabled={!inputReady}
              >
                Далее »
              </button>
            </div>
          </div>
        )}

        {props.resultType === "image" && props.capability && props.step === "params" && (
          <div className="params-step-layout panel">
            <div className="parameter-summary" role="note" aria-label="Доступные параметры">
              <div className="parameter-summary-heading">
                <div>
                  <span className="dock-label">Доступные параметры</span>
                  <strong>Что можно настроить</strong>
                </div>
                <span className="parameter-summary-hint">Настройки ниже</span>
              </div>
              <div className="parameter-cloud">
                {supportedParameters.slice(0, 10).map((parameter) => (
                  <span key={parameter}>{formatParameterSummaryName(parameter)}</span>
                ))}
              </div>
            </div>

            {props.capability.startsWith("image.") && (
              <>
                {supportedParameters.includes("aspect_ratio") ? (
                  <div className="dock-section image-parameter-section">
                    <div className="parameter-section-heading">
                      <div>
                        <span className="dock-label">Формат</span>
                        <small>Как будет выглядеть готовое изображение</small>
                      </div>
                      <strong>{props.aspectRatio}</strong>
                    </div>
                    <div className="ratio-grid" role="radiogroup" aria-label="Формат изображения">
                      {supportedRatios.map((ratio) => (
                        <button
                          key={ratio}
                          type="button"
                          role="radio"
                          aria-checked={props.aspectRatio === ratio}
                          className={`ratio-option ${props.aspectRatio === ratio ? "active" : ""}`}
                          data-ratio={ratio}
                          onClick={() => props.setAspectRatio(ratio)}
                        >
                          <span className="ratio-shape" aria-hidden="true" />
                          <span className="ratio-label">{ratio}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="dock-section parameter-unavailable">
                    <span className="dock-label">Формат</span>
                    <div>Эта модель использует один формат.</div>
                  </div>
                )}

                {supportedParameters.includes("quality") ? (
                  <div className="dock-section image-parameter-section">
                    <div className="parameter-section-heading">
                      <div>
                        <span className="dock-label">Качество</span>
                        <small>Больше деталей может потребовать больше времени</small>
                      </div>
                      <strong>{formatQualityLabel(props.imageQuality)}</strong>
                    </div>
                    <div className="segmented quality-options" role="radiogroup" aria-label="Качество изображения">
                      {["standard", "hd"].map((q) => (
                        <button
                          key={q}
                          type="button"
                          role="radio"
                          aria-checked={props.imageQuality === q}
                          className={props.imageQuality === q ? "active" : ""}
                          onClick={() => props.setImageQuality(q)}
                        >
                          {formatQualityLabel(q)}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="dock-section parameter-unavailable">
                    <span className="dock-label">Качество</span>
                    <div>Качество нельзя изменить для этой модели.</div>
                  </div>
                )}

                {supportedParameters.includes("n") ? (
                  <div className="dock-section image-parameter-section">
                    <div className="parameter-section-heading">
                      <div>
                        <span className="dock-label">Количество вариантов</span>
                        <small>Получить несколько вариантов за один запуск</small>
                      </div>
                      <strong>{props.nVariants}</strong>
                    </div>
                    <div className="variant-options" role="radiogroup" aria-label="Количество вариантов">
                      {[1, 2, 4].map((v) => (
                        <button
                          key={v}
                          type="button"
                          role="radio"
                          aria-checked={props.nVariants === v}
                          className={props.nVariants === v ? "active" : ""}
                          onClick={() => props.setNVariants(v)}
                        >
                          {v}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="dock-section parameter-unavailable">
                    <span className="dock-label">Количество вариантов</span>
                    <div>Эта модель создаёт один вариант за запуск.</div>
                  </div>
                )}

                {supportedParameters.includes("negative_prompt") ? (
                  <div className="dock-section image-parameter-section">
                    <span className="dock-label">Что не добавлять</span>
                    <input
                      type="text"
                      aria-label="Что не добавлять"
                      placeholder="Например: текст, лишние детали, размытый фон"
                      value={props.negativePrompt}
                      onChange={(e) => props.setNegativePrompt(e.target.value)}
                    />
                  </div>
                ) : (
                  <div className="dock-section parameter-unavailable">
                    <span className="dock-label">Что не добавлять</span>
                    <div>Для этой модели исключения из описания недоступны.</div>
                  </div>
                )}
              </>
            )}

            {props.capability.startsWith("text.") && props.capability !== "text.prompt_builder" && (
              <div className="dock-section" style={{ marginBottom: "20px" }}>
                <span className="dock-label">Стиль текста</span>
                <div className="segmented" style={{ display: "flex", gap: "4px", background: "var(--bg-dark)", padding: "4px", borderRadius: "8px", marginTop: "8px" }}>
                  {tones.map((tone) => (
                    <button
                      key={tone}
                      className={props.textTone === tone ? "active" : ""}
                      onClick={() => props.setTextTone(tone)}
                      style={{ flex: 1, padding: "8px", border: "none", borderRadius: "6px", background: props.textTone === tone ? "var(--bg-card)" : "transparent", color: "var(--foreground)", cursor: "pointer" }}
                    >
                      {tone}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {props.capability === "text.prompt_builder" && (
              <div className="dock-section" style={{ marginBottom: "20px" }}>
                <span className="dock-label">Для чего промпт</span>
                <select
                  value={props.promptTargetCapability}
                  onChange={(event) => props.setPromptTargetCapability(event.target.value as Capability)}
                  style={{ width: "100%", padding: "10px", borderRadius: "8px", background: "var(--bg-card)", border: "1px solid var(--border)", color: "var(--foreground)", marginTop: "8px" }}
                >
                  <option value="image.text_to_image">Изображение</option>
                  <option value="image.image_to_image">Изображение по образцу</option>
                  <option value="text.chat">Текст</option>
                </select>
              </div>
            )}

            <div className="wizard-navigation">
              <button className="secondary" onClick={() => props.setStep("input")}>« Назад</button>
              <button 
                className="primary" 
                onClick={() => {
                  props.setStep("review");
                  props.onDraft();
                }}
              >
                Далее »
              </button>
            </div>
          </div>
        )}

        {props.resultType === "image" && props.capability && props.scenario && props.step === "review" && (
          <div className="review-step-layout">
            <section className="launch-review panel">
              <div className="launch-copy">
                <span>Стоимость создания</span>
                <strong style={{ fontSize: "24px", color: "var(--teal)" }}>{props.estimating ? "Считаем..." : formattedCost}</strong>
                <p>Баланс списывается только после успешного создания.</p>
              </div>
              
              <div className="launch-invoice-box" style={{ margin: "20px 0", borderTop: "1px solid var(--border)", borderBottom: "1px solid var(--border)", padding: "16px 0" }}>
                <h4 style={{ margin: "0 0 12px 0", color: "var(--muted)", textTransform: "uppercase", fontSize: "11px", letterSpacing: "1px" }}>Параметры создания</h4>
                <div style={{ display: "grid", gap: "10px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ color: "var(--muted)" }}>Результат:</span>
                    <strong>{props.resultType === "image" ? "Изображение" : "Видео"}</strong>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ color: "var(--muted)" }}>Задача:</span>
                    <strong>{modeTitle}</strong>
                  </div>
                  {props.scenario.inputKind === "reference" && (
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ color: "var(--muted)" }}>Образец:</span>
                      <strong>Добавлен</strong>
                    </div>
                  )}
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ color: "var(--muted)" }}>Модель:</span>
                    <strong>{IMAGE_MODEL_LABEL}</strong>
                  </div>
                  
                  {props.capability.startsWith("image.") && (
                    <>
                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <span style={{ color: "var(--muted)" }}>Формат:</span>
                        <strong>{props.aspectRatio}</strong>
                      </div>
                      {props.imageQuality !== "standard" && (
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                          <span style={{ color: "var(--muted)" }}>Качество:</span>
                          <strong>{formatQualityLabel(props.imageQuality)}</strong>
                        </div>
                      )}
                      {props.nVariants > 1 && (
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <span style={{ color: "var(--muted)" }}>Версии:</span>
                          <strong>{props.nVariants}</strong>
                        </div>
                      )}
                    </>
                  )}
                  {props.capability.startsWith("text.") && props.capability !== "text.prompt_builder" && (
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ color: "var(--muted)" }}>Стиль текста:</span>
                      <strong>{props.textTone}</strong>
                    </div>
                  )}
                  {props.capability === "text.prompt_builder" && (
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ color: "var(--muted)" }}>Цель:</span>
                      <strong>{getCapabilityTitle(props.promptTargetCapability)}</strong>
                    </div>
                  )}
                </div>
              </div>
              
              {!props.busy && (
                <div className="wizard-navigation">
                  <button className="secondary" onClick={() => props.setStep("params")}>« Назад</button>
                  <button
                    className="primary launch-button"
                    onClick={props.onGenerate}
                    disabled={!canLaunch}
                  >
                    <Wand2 size={18} />
                    {props.isAuthenticated ? (props.estimatedCost ? "Создать" : "Рассчитать и создать") : "Откройте в Telegram"}
                  </button>
                </div>
              )}
              {props.generationError && (
                <div className="inline-generation-error" role="alert">
                  <strong>{props.estimatedCost ? "Не удалось создать изображение" : "Не удалось рассчитать стоимость"}</strong>
                  <span>{props.generationError}</span>
                  <button className="secondary retry-generation-button" onClick={props.onGenerate} disabled={props.busy}>
                    <Wand2 size={15} /> {props.estimatedCost ? "Попробовать снова" : "Рассчитать ещё раз"}
                  </button>
                </div>
              )}
            </section>
          </div>
        )}
      </section>

      {/* Manual Model Selection Overlay */}
      {props.showModelOverlay && (
        <div className="modal-overlay" onClick={() => props.setShowModelOverlay(false)}>
          <div className="modal-content" role="dialog" aria-modal="true" aria-labelledby="model-dialog-title" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px", borderBottom: "1px solid var(--border)" }}>
              <h3 id="model-dialog-title">Выберите модель</h3>
              <button className="secondary compact-button" onClick={() => props.setShowModelOverlay(false)}>Закрыть</button>
            </div>
            <div className="modal-body-scroll" style={{ padding: "16px", maxHeight: "70vh", overflowY: "auto" }}>
              <ModelCatalog
                models={props.models}
                isAuthenticated={props.isAuthenticated}
                loading={false}
                providers={props.providers}
                query={props.query}
                setQuery={props.setQuery}
                provider={props.provider}
                setProvider={props.setProvider}
                sort={props.sort}
                setSort={props.setSort}
                runnableCapabilities={props.runnableCapabilities}
                lockedCapability={props.capability}
                selectedModel={props.currentModel?.model_id ?? null}
                onSelect={(modelId) => {
                  props.onSelectModel(modelId);
                  props.setShowModelOverlay(false);
                }}
                onUseAuto={() => {
                  props.onUseAutoModel();
                  props.setShowModelOverlay(false);
                }}
                onResetFilters={() => {
                  props.setQuery("");
                  props.setProvider("all");
                  props.setSort("recommended");
                }}
              />
            </div>
          </div>
        </div>
      )}

    </section>
  );
}

function ContentTypeRail({
  types,
  availableTypes,
  activeType,
  onSelect
}: {
  types: ResultTypeDefinition[];
  availableTypes: ResultType[];
  activeType: ResultType | null;
  onSelect: (type: ResultType) => void;
}) {
  return (
    <div className="content-type-rail" aria-label="Тип контента" role="tablist">
      {types.map((type) => {
        const available = availableTypes.includes(type.id);
        const comingSoon = type.id === "audio" || type.id === "video";
        const isActive = available && activeType === type.id;
        return (
          <button
            key={type.id}
            role="tab"
            aria-label={type.title}
            aria-selected={isActive}
            aria-disabled={!available}
            disabled={!available}
            className={`content-type-card type-${type.id} ${isActive ? "active" : ""} ${!available ? "unavailable" : ""}`}
            onClick={() => available && onSelect(type.id)}
          >
            <span className="content-type-icon">{type.icon}</span>
            <span className="content-type-title">{type.title}</span>
            <small>{comingSoon ? "Скоро" : type.id === "text" ? "Чат" : type.id === "prompt" ? "Запрос" : type.id === "image" ? "Создать" : "Скоро"}</small>
          </button>
        );
      })}
    </div>
  );
}

function TextWorkspace({
  turns,
  conversations,
  activeConversationId,
  onConversationChange,
  onNewConversation,
  input,
  onInputChange,
  attachments,
  onUpload,
  onRemoveAttachment,
  onSend,
  busy,
  isAuthenticated,
  currentModel,
  recommendedModels,
  modelSelectionMode,
  onSelectModel,
  onUseAutoModel,
  openModels,
  estimatedCost,
  estimating
}: {
  turns: TextTurn[];
  conversations: TextConversation[];
  activeConversationId: string;
  onConversationChange: (id: string) => void;
  onNewConversation: () => void;
  input: string;
  onInputChange: (value: string) => void;
  attachments: TextAttachment[];
  onUpload: (file: File) => void;
  onRemoveAttachment: (attachment: TextAttachment) => void;
  onSend: () => void;
  busy: boolean;
  isAuthenticated: boolean;
  currentModel: ModelItem | null;
  recommendedModels: ModelItem[];
  modelSelectionMode: "auto" | "trend" | "cheap" | "fast" | "quality" | "manual";
  onSelectModel: (id: string) => void;
  onUseAutoModel: () => void;
  openModels: () => void;
  estimatedCost?: string | null;
  estimating: boolean;
}) {
  const canSend = Boolean(input.trim()) && isAuthenticated && !busy;
  const turnsContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Keep the page position stable when the Text workspace first opens. The
    // old scrollIntoView call moved the whole Mini App down to an empty
    // dialogue; only the dialogue's own scroll area should follow new turns.
    if (!turns.length && !busy) return;
    const container = turnsContainerRef.current;
    if (container) container.scrollTop = container.scrollHeight;
  }, [turns, busy]);
  return (
    <div className="text-workspace">
      <aside className="text-history panel" aria-label="История диалогов">
        <div className="text-history-heading">
          <div>
            <span className="dock-label">Диалоги</span>
            <strong>История</strong>
          </div>
          <button className="icon-button" aria-label="Новый диалог" onClick={onNewConversation}><PlusIcon /></button>
        </div>
        <div className="text-history-list">
          {conversations.map((conversation) => (
            <button
              key={conversation.id}
              className={`text-history-item ${conversation.id === activeConversationId ? "active" : ""}`}
              onClick={() => onConversationChange(conversation.id)}
            >
              <History size={15} />
              <span>{conversation.title}</span>
              <small>{conversation.turns.length > 0 ? formatDate(conversation.updatedAt) : "Новый"}</small>
            </button>
          ))}
        </div>
      </aside>

      <section className="text-dialogue panel">
        <div className="text-dialogue-toolbar">
          <div>
            <span className="dock-label">Диалог</span>
            <strong>{turns.length > 0 ? "Продолжайте диалог" : "Начните с задачи"}</strong>
          </div>
          <button className="secondary compact-button" onClick={onNewConversation}>Новый диалог</button>
        </div>
        <div className="text-turns" ref={turnsContainerRef} aria-live="polite">
          {turns.length === 0 ? (
            <div className="text-empty-state">
              <MessageSquareText size={30} />
              <strong>Начните с сообщения</strong>
              <p>Напишите задачу обычными словами. Можно прикрепить изображение, PDF или текстовый файл, а затем продолжить диалог.</p>
              <div className="text-suggestions">
                {['Напишите пост о запуске продукта', 'Сократите и упростите текст', 'Напишите деловое письмо'].map((suggestion) => (
                  <button key={suggestion} className="suggestion-chip" onClick={() => onInputChange(suggestion)}>{suggestion}</button>
                ))}
              </div>
            </div>
          ) : turns.map((turn) => (
            <article key={turn.id} className={`text-turn ${turn.role}`}>
              <div className="text-turn-label">{turn.role === "user" ? "Вы" : turn.role === "assistant" ? "Znich" : "Система"}</div>
              <div className="text-turn-content">{turn.content}</div>
              {turn.attachments?.length ? (
                <div className="text-turn-attachments">
                  {turn.attachments.map((attachment) => <span key={`${turn.id}-${attachment.name}`}><FileText size={13} />{attachment.name}</span>)}
                </div>
              ) : null}
            </article>
          ))}
          {busy && <div className="text-turn assistant pending"><div className="text-turn-label">Znich</div><div className="text-turn-content"><Loader2 className="spin" size={16} /> Готовим ответ…</div></div>}
        </div>
        <div className="text-composer">
          <textarea
            value={input}
            onChange={(event) => onInputChange(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                onSend();
              }
            }}
            placeholder="Введите сообщение… Enter — отправить, Shift+Enter — новая строка"
            aria-label="Сообщение для диалога"
          />
          <div className="text-composer-footer">
            <div className="text-composer-tools">
              <label className={`attachment-button ${!isAuthenticated ? "disabled" : ""}`} aria-disabled={!isAuthenticated} title={isAuthenticated ? "Прикрепить файл" : "Откройте Znich в Telegram, чтобы прикрепить файл"}>
                <Paperclip size={17} />
                <span>Прикрепить</span>
                <input type="file" accept="image/*,.txt,.md,.csv,.json,.pdf,.docx" hidden onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) onUpload(file);
                  event.currentTarget.value = "";
                }} disabled={!isAuthenticated} />
              </label>
              {attachments.map((attachment) => (
                <button
                  type="button"
                  className="pending-attachment"
                  key={`${attachment.name}-${attachment.size}`}
                  title={`Удалить ${attachment.name}`}
                  aria-label={`Удалить вложение ${attachment.name}`}
                  onClick={() => onRemoveAttachment(attachment)}
                >
                  <FileUp size={13} />
                  <span>{attachment.name}</span>
                  <strong aria-hidden="true">×</strong>
                </button>
              ))}
            </div>
            <button className="primary send-button" disabled={!canSend} onClick={onSend}><Send size={17} />Отправить</button>
          </div>
        </div>
      </section>

      <aside className="text-controls panel">
        <div className="text-control-block">
          <span className="dock-label">Модель</span>
          <button
            type="button"
            className="text-model-current"
            aria-label={`Выбрать модель для диалога. Сейчас: ${TEXT_MODEL_LABEL}`}
            onClick={openModels}
            disabled={!isAuthenticated}
          >
            <span><Sparkles size={16} /><strong>{TEXT_MODEL_LABEL}</strong></span>
            <small>{isAuthenticated ? "Сменить модель" : "Откройте Telegram"}</small>
          </button>
        </div>
        <div className="text-cost-block">
          <span>Стоимость ответа</span>
          <strong>{estimating ? "Рассчитываем…" : estimatedCost ? formatTokens(estimatedCost) : "После ввода сообщения"}</strong>
          <small>Токены списываются только после успешного ответа.</small>
        </div>
      </aside>
    </div>
  );
}

function PromptWorkspace({
  prompt,
  setPrompt,
  output,
  setOutput,
  targetCapability,
  setTargetCapability,
  currentModel,
  recommendedModels,
  onSelectModel,
  onUseAutoModel,
  openModels,
  onGenerate,
  busy,
  isAuthenticated
}: {
  prompt: string;
  setPrompt: (value: string) => void;
  output: string;
  setOutput: (value: string) => void;
  targetCapability: Capability;
  setTargetCapability: (value: Capability) => void;
  currentModel: ModelItem | null;
  recommendedModels: ModelItem[];
  onSelectModel: (id: string) => void;
  onUseAutoModel: () => void;
  openModels: () => void;
  onGenerate: () => void;
  busy: boolean;
  isAuthenticated: boolean;
}) {
  const [copiedOutput, setCopiedOutput] = useState(false);

  async function copyOutput() {
    if (!navigator.clipboard?.writeText) return;
    try {
      await navigator.clipboard.writeText(output);
      setCopiedOutput(true);
      window.setTimeout(() => setCopiedOutput(false), 1800);
    } catch {
      // Clipboard permissions can be unavailable in embedded webviews.
    }
  }

  return (
    <div className="prompt-workspace">
      <section className="prompt-editor panel">
        <div className="prompt-workspace-heading"><div><span className="dock-label">Промпт-студия</span><strong>Из идеи — в готовый запрос</strong></div><span className="workspace-status">{isAuthenticated ? "Готово к работе" : "Только просмотр"}</span></div>
        <label className="prompt-target-field"><span>Для чего нужен промпт</span><select value={targetCapability} onChange={(event) => setTargetCapability(event.target.value as Capability)}><option value="image.text_to_image">Изображение</option><option value="image.image_to_image">Изображение по образцу</option><option value="text.chat">Текст</option><option value="video.text_to_video">Видео</option></select></label>
        <label className="prompt-large-field"><span>Что нужно получить</span><textarea value={prompt} onChange={(event) => setPrompt(event.target.value)} placeholder="Например: рекламный кадр для премиального сервиса…" /></label>
        <div className="prompt-editor-footer"><span>{prompt.length} символов</span><button className="primary" disabled={!isAuthenticated || busy || prompt.trim().length < 3} onClick={onGenerate}>{busy ? <Loader2 className="spin" size={17} /> : <Wand2 size={17} />}Создать промпт</button></div>
      </section>
      <section className="prompt-output panel">
        <div className="prompt-workspace-heading"><div><span className="dock-label">Результат</span><strong>Ваш промпт</strong></div>{output && <button className="secondary compact-button" onClick={() => void copyOutput()}><Copy size={15} />{copiedOutput ? "Скопировано" : "Скопировать"}</button>}</div>
        <textarea value={output} onChange={(event) => setOutput(event.target.value)} placeholder="Здесь появится промпт. Его можно изменить и скопировать." aria-label="Результат создания промпта" />
        <button type="button" className="prompt-model-strip prompt-model-button" onClick={openModels} disabled={!isAuthenticated} aria-label={`Выбрать модель для промпта. Сейчас: ${TEXT_MODEL_LABEL}`}>
          <span>Модель</span>
          <strong>{TEXT_MODEL_LABEL}</strong>
          <small>{isAuthenticated ? "Нажмите, чтобы выбрать другую" : "Откройте Znich в Telegram"}</small>
          <ChevronRight size={14} aria-hidden="true" />
        </button>
      </section>
    </div>
  );
}

function AudioWorkspace({
  scenarios,
  runnableCapabilities,
  selectedScenario,
  prompt,
  setPrompt,
  uploadPreview,
  onUpload,
  isAuthenticated,
  onSelectScenario
}: {
  scenarios: CreationScenario[];
  runnableCapabilities: Set<Capability>;
  selectedScenario: ScenarioId;
  prompt: string;
  setPrompt: (value: string) => void;
  uploadPreview: string | null;
  onUpload: (file: File) => void;
  isAuthenticated: boolean;
  onSelectScenario: (scenarioId: ScenarioId) => void;
}) {
  const audioScenarios = scenarios.filter((scenario) => scenario.resultType === "audio");
  const selected = audioScenarios.find((scenario) => scenario.id === selectedScenario) ?? audioScenarios[0];
  const available = selected ? runnableCapabilities.has(selected.capability) : false;
  const needsAudioFile = selected?.inputKind === "reference";
  const inputLabel = selected?.id === "audio.text_to_speech" ? "Текст для озвучивания" : "Что сделать с аудиозаписью";
  const inputPlaceholder = selected?.id === "audio.text_to_speech"
    ? "Введите текст, который хотите озвучить…"
    : "Например: сделайте голос спокойнее и уберите шумы";

  return (
    <section className="audio-workspace panel">
      <div className="audio-workspace-heading"><div><span className="dock-label">Аудио</span><strong>Звук появится позже</strong></div><span className="workspace-status">Скоро</span></div>
      <div className="audio-route-grid">
        {audioScenarios.map((scenario) => {
          const available = runnableCapabilities.has(scenario.capability);
          return (
            <button key={scenario.id} type="button" disabled={!available} className={`audio-route ${available && selectedScenario === scenario.id ? "active" : ""} ${available ? "available" : "coming"}`} onClick={() => available && onSelectScenario(scenario.id)}>
              <span className="audio-route-icon">{scenario.id === "audio.text_to_speech" ? <Volume2 size={22} /> : scenario.id === "audio.speech_to_speech" ? <Mic size={22} /> : <FileText size={22} />}</span>
              <strong>{scenario.title}</strong>
              <small>{scenario.description}</small>
              <em>{available ? "Доступно" : "Скоро"}</em>
            </button>
          );
        })}
      </div>
      {selected && !available && (
        <div className="audio-unavailable-state" role="status">
          <Volume2 size={20} />
          <div>
            <strong>Аудио появится позже</strong>
            <span>Сейчас в Znich доступны изображения, тексты и промпты.</span>
          </div>
        </div>
      )}
      {selected && available && (
        <div className="audio-route-form">
          <div className="audio-route-form-heading">
            <div><span className="dock-label">Выбрано</span><strong>{selected.title}</strong></div>
            <span className={`workspace-status ${available ? "available" : "unavailable"}`}>{available ? "Доступно" : "Скоро"}</span>
          </div>
          {needsAudioFile && (
            <label className="audio-file-field">
              <Mic size={18} />
              <span>{uploadPreview ? "Аудиофайл добавлен" : "Загрузить аудиофайл"}</span>
              <small>{uploadPreview ? "Готово к обработке" : "Поддерживаются MP3, WAV, M4A и голосовые сообщения"}</small>
              <input type="file" accept="audio/*" hidden disabled={!isAuthenticated || !available} onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) onUpload(file);
                event.currentTarget.value = "";
              }} />
            </label>
          )}
          <label className="audio-input-field">
            <span>{inputLabel}</span>
            <textarea value={prompt} onChange={(event) => setPrompt(event.target.value)} disabled={!isAuthenticated || !available} placeholder={inputPlaceholder} />
          </label>
          <div className="audio-form-footer">
            <span>{available ? "Заполните поле, чтобы рассчитать стоимость." : "Этот режим пока недоступен. Баланс не списывается."}</span>
            <button className="primary" disabled>
              Скоро
            </button>
          </div>
        </div>
      )}
      <div className="audio-route-note"><Mic size={17} /><span>Позже здесь можно будет озвучивать текст, менять голос и расшифровывать записи.</span></div>
    </section>
  );
}

function PlusIcon() {
  return <span aria-hidden="true" className="plus-icon">+</span>;
}

function UploadManager({ onUpload, preview, emphasized, disabled = false }: { onUpload: (file: File) => void; preview: string | null; emphasized?: boolean; disabled?: boolean }) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <div
      className={`upload-box ${emphasized ? "emphasized" : ""} ${disabled ? "disabled" : ""}`}
      aria-disabled={disabled}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        if (disabled) return;
        const file = e.dataTransfer.files.item(0);
        if (file) onUpload(file);
      }}
    >
      {preview ? <AuthorizedImage source={preview} alt="Образец" /> : <Upload size={24} />}
      <strong>{preview ? "Изображение добавлено" : "Загрузите образец"}</strong>
      <span>Перетащите файл или выберите с диска</span>
      <button className="secondary" disabled={disabled} onClick={() => inputRef.current?.click()}>
        {disabled ? "Откройте в Telegram" : "Выбрать файл"}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        hidden
        disabled={disabled}
        onChange={(e) => {
          const file = e.target.files?.item(0);
          if (file) onUpload(file);
        }}
      />
    </div>
  );
}

function ModelCatalog(props: {
  models: ModelItem[];
  isAuthenticated: boolean;
  loading: boolean;
  providers: string[];
  query: string;
  setQuery: (value: string) => void;
  provider: string;
  setProvider: (value: string) => void;
  sort: string;
  setSort: (value: string) => void;
  runnableCapabilities: Set<Capability>;
  lockedCapability?: Capability | null;
  selectedModel: string | null;
  onSelect: (modelId: string) => void;
  onUseAuto?: () => void;
  onResetFilters: () => void;
}) {
  const [activeTab, setActiveTab] = useState<string>("all");
  const [selectedCatalogModel, setSelectedCatalogModel] = useState<ModelItem | null>(null);
  const [stats, setStats] = useState<ModelsStats | null>(null);

  useBodyScrollLock(Boolean(selectedCatalogModel));
  useDialogFocus(Boolean(selectedCatalogModel));

  useEffect(() => {
    if (props.isAuthenticated && !props.lockedCapability) {
      fetchModelsStats().then(setStats).catch((err) => console.error("fetchModelsStats failed:", err));
    }
  }, [props.isAuthenticated, props.lockedCapability]);

  if (!props.isAuthenticated) {
    return (
      <EmptyState
        icon={<SlidersHorizontal size={28} />}
        title="Войдите через Telegram"
        text="Откройте Znich в Telegram, чтобы увидеть модели, стоимость и доступность."
      />
    );
  }

  const filteredModelsList = useMemo(() => {
    const publicModels = props.models.filter((model) => !isInternalModel(model));
    if (props.lockedCapability) {
      return sortModels(filterModels(publicModels, props.query, props.provider, props.lockedCapability), props.sort);
    } else {
      return sortModels(filterCatalogModels(publicModels, props.query, props.provider, activeTab), props.sort);
    }
  }, [props.models, props.query, props.provider, props.sort, props.lockedCapability, activeTab]);

  const tabs = [
    { id: "all", title: "Все" },
    { id: "text", title: "Текст" },
    { id: "image", title: "Изображения" },
    { id: "video", title: "Видео" },
    { id: "audio", title: "Аудио" },
    { id: "file", title: "Файлы" },
    { id: "json", title: "JSON / Tools" }
  ];

  function formatModality(m: string): string {
    switch (m) {
      case "text": return "текст";
      case "image": return "изображения";
      case "video": return "видео";
      case "audio": return "аудио";
      case "file": return "файлы / документы";
      default: return m;
    }
  }

  return (
    <section className="model-catalog-screen">
      {selectedCatalogModel && (
        <div className="modal-overlay" onClick={() => setSelectedCatalogModel(null)} style={{ zIndex: 1100 }}>
          <div className="modal-content" role="dialog" aria-modal="true" aria-labelledby="catalog-model-dialog-title" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "500px" }}>
            <div className="modal-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px", borderBottom: "1px solid var(--border)" }}>
              <h3 id="catalog-model-dialog-title" style={{ margin: 0 }}>О модели</h3>
              <button className="secondary compact-button" onClick={() => setSelectedCatalogModel(null)}>Закрыть</button>
            </div>
            <div className="modal-body-scroll" style={{ padding: "20px", maxHeight: "75vh", overflowY: "auto", display: "flex", flexDirection: "column", gap: "16px" }}>
              <div>
                <strong style={{ fontSize: "18px", color: "var(--foreground)" }}>{getCatalogModelLabel(selectedCatalogModel)}</strong>
                {!isInternalModel(selectedCatalogModel) && (
                  <div style={{ color: "var(--muted)", fontSize: "13px", marginTop: "4px", fontFamily: "monospace" }}>{selectedCatalogModel.model_id}</div>
                )}
              </div>

              {selectedCatalogModel.description && (
                <p style={{ fontSize: "14px", lineHeight: "1.6", color: "var(--text)" }}>{selectedCatalogModel.description}</p>
              )}

              {(() => {
                const isRunnable = Array.from(props.runnableCapabilities).some((cap) => supportsCapability(selectedCatalogModel, cap as Capability));
                if (!isRunnable) {
                  return (
                    <div style={{ background: "rgba(244, 63, 94, 0.1)", border: "1px solid rgba(244, 63, 94, 0.2)", padding: "12px", borderRadius: "8px", display: "flex", alignItems: "center", gap: "10px" }}>
                      <Award size={18} style={{ color: "var(--rose)", flexShrink: 0 }} />
                      <div style={{ fontSize: "13px", color: "var(--rose)" }}>
                        <strong>Есть в каталоге</strong><br />
                        Создание через Znich пока недоступно
                      </div>
                    </div>
                  );
                }
                return null;
              })()}

              <div className="panel" style={{ padding: "14px", background: "var(--bg-dark)", gap: "10px" }}>
                <div style={{ display: "grid", gridTemplateColumns: "140px 1fr", gap: "8px 12px", fontSize: "13px" }}>
                  <span style={{ color: "var(--muted)" }}>Разработчик:</span>
                  <strong>{selectedCatalogModel.provider.toLowerCase() === "agy" ? "Znich" : selectedCatalogModel.provider}</strong>

                  <span style={{ color: "var(--muted)" }}>Принимает:</span>
                  <span>{selectedCatalogModel.input_modalities.map(formatModality).join(", ") || "нет"}</span>

                  <span style={{ color: "var(--muted)" }}>Создаёт:</span>
                  <span>{selectedCatalogModel.output_modalities.map(formatModality).join(", ") || "нет"}</span>

                  <span style={{ color: "var(--muted)" }}>Стоимость:</span>
                  <strong style={{ color: "var(--gold)" }}>{formatTokens(selectedCatalogModel.estimated_cost)}</strong>

                  <span style={{ color: "var(--muted)" }}>Контекст:</span>
                  <span>{selectedCatalogModel.context_length ? `${selectedCatalogModel.context_length.toLocaleString()} токенов` : "не указан"}</span>

                  <span style={{ color: "var(--muted)" }}>Поддерживает:</span>
                  <span>{selectedCatalogModel.supported_parameters.map(formatParameterName).join(", ") || "стандартные"}</span>
                </div>
              </div>

              {selectedCatalogModel.metadata?.per_request_limits && (
                <div style={{ fontSize: "12px", color: "var(--muted)" }}>
                  <strong>Ограничения:</strong> {JSON.stringify(selectedCatalogModel.metadata.per_request_limits)}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="catalog-hero">
        <div>
          <span className="eyebrow">Каталог</span>
          <h2>Выберите модель</h2>
          <p>Автоподбор подойдёт для большинства задач. Выберите модель вручную, если важны конкретный разработчик, стоимость или возможности.</p>
          {stats && (
            <div style={{ display: "flex", gap: "16px", marginTop: "12px", fontSize: "12px", color: "var(--muted)" }}>
              <span>Всего моделей: <strong>{stats.total_models}</strong></span>
              <span>Разработчиков: <strong>{stats.total_providers}</strong></span>
              {stats.last_sync_at && <span>Обновлено: <strong>{new Date(stats.last_sync_at).toLocaleDateString()}</strong></span>}
            </div>
          )}
        </div>
        {props.onUseAuto && (
          <button className={`auto-card ${props.selectedModel === null ? "active" : ""}`} onClick={props.onUseAuto}>
            <Sparkles size={18} />
            <strong>Авто</strong>
            <span>Подберём модель под вашу задачу</span>
          </button>
        )}
      </div>

      {!props.lockedCapability && (
        <div className="modality-tabs" style={{ display: "flex", gap: "8px", overflowX: "auto", paddingBottom: "8px", marginBottom: "16px", borderBottom: "1px solid var(--border)" }}>
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  padding: "6px 12px",
                  fontSize: "13px",
                  borderRadius: "20px",
                  background: isActive ? "rgba(6, 182, 212, 0.15)" : "transparent",
                  color: isActive ? "var(--teal)" : "var(--muted)",
                  border: isActive ? "1px solid rgba(6, 182, 212, 0.3)" : "1px solid transparent",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  minHeight: "auto"
                }}
              >
                {tab.title}
              </button>
            );
          })}
        </div>
      )}

      <div className="catalog-toolbar">
        <label className="search-field">
          <Search size={16} />
          <input
            value={props.query}
            onChange={(e) => props.setQuery(e.target.value)}
            placeholder="Поиск модели..."
          />
        </label>
        <select value={props.provider} onChange={(e) => props.setProvider(e.target.value)}>
          {props.providers.map((item) => (
            <option key={item} value={item}>
              {item === "all" ? "Все разработчики" : item}
            </option>
          ))}
        </select>
        <select value={props.sort} onChange={(e) => props.setSort(e.target.value)}>
          <option value="recommended">Рекомендуемые</option>
          <option value="cheap">Сначала недорогие</option>
          <option value="fast">Сначала быстрые</option>
          <option value="quality">Сначала качественные</option>
          <option value="provider">По разработчику</option>
        </select>
      </div>

      {props.loading ? (
        <SkeletonList />
      ) : filteredModelsList.length === 0 ? (
        <div className="empty-state action-empty">
          <Search size={28} />
          <strong>Модели не найдены</strong>
          <p>Сбросьте фильтры или включите автоматический выбор модели.</p>
          <div className="empty-actions">
            {props.onUseAuto && <button className="primary" onClick={props.onUseAuto}>Включить автоподбор</button>}
            <button className="secondary" onClick={props.onResetFilters}>Сбросить фильтры</button>
          </div>
        </div>
      ) : (
        <div className="model-list">
          {filteredModelsList.map((model) => {
            const isRunnable = Array.from(props.runnableCapabilities).some((cap) => supportsCapability(model, cap as Capability));
            return (
              <article
                key={model.model_id}
                className={`model-row ${props.selectedModel === model.model_id ? "selected" : ""}`}
                style={{ cursor: "pointer" }}
                onClick={() => setSelectedCatalogModel(model)}
              >
                <div style={{ display: "flex", flexDirection: "column", gap: "4px", flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <strong>{getCatalogModelLabel(model)}</strong>
                    {!isRunnable && (
                      <span style={{ background: "rgba(244, 63, 94, 0.1)", color: "var(--rose)", fontSize: "10px", padding: "2px 6px", borderRadius: "4px", fontWeight: "bold" }}>
                        только в каталоге
                      </span>
                    )}
                  </div>
                  <span style={{ fontSize: "12px", color: "var(--muted)", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                    {model.description || model.provider}
                  </span>
                </div>
                <div className="model-meta" style={{ minWidth: "120px" }}>
                  <span>~{Number(model.estimated_cost).toFixed(2)} токенов</span>
                  <span>Качество: {Math.round(model.quality_score * 10)}/10</span>
                </div>
                {isRunnable ? (
                  <button
                    className="secondary"
                    onClick={(e) => {
                      e.stopPropagation();
                      props.onSelect(model.model_id);
                    }}
                  >
                    Выбрать
                  </button>
                ) : (
                  <button
                    className="secondary ghost-button"
                    style={{ opacity: 0.7 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedCatalogModel(model);
                    }}
                  >
                    Инфо
                  </button>
                )}
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}

function GalleryScreen({
  tasks,
  selectedTask,
  isAuthenticated,
  loading,
  onCreate,
  onOpen,
  onCloseDetail,
  onReuse,
  onUseAsReference,
  onAction,
  onDelete
}: {
  tasks: Task[];
  selectedTask: Task | null;
  isAuthenticated: boolean;
  loading: boolean;
  onCreate: () => void;
  onOpen: (task: Task) => void;
  onCloseDetail: () => void;
  onReuse: (task: Task) => void;
  onUseAsReference: (task: Task) => void;
  onAction: (task: Task, action: string) => void;
  onDelete: (taskId: string) => void;
}) {
  const [filter, setFilter] = useState<"all" | "image" | "text" | "prompt">("all");

  const filteredTasks = useMemo(() => {
    if (filter === "all") return tasks;
    if (filter === "image") return tasks.filter((t) => (typeof t.capability === "string" ? t.capability : "").startsWith("image."));
    if (filter === "text") return tasks.filter((t) => {
      const capability = typeof t.capability === "string" ? t.capability : "";
      return capability.startsWith("text.") && capability !== "text.prompt_builder";
    });
    if (filter === "prompt") return tasks.filter((t) => t.capability === "text.prompt_builder");
    return tasks;
  }, [tasks, filter]);

  if (!isAuthenticated) {
    return (
      <EmptyState
        icon={<GalleryHorizontalEnd size={28} />}
        title="Войдите через Telegram"
        text="Откройте Znich в Telegram, чтобы увидеть историю работ и сохранённые файлы."
      />
    );
  }

  return (
    <section className="gallery-space">
      <div className="gallery-screen-heading">
        <div>
          <span className="eyebrow">Галерея</span>
          <h2>Ваши результаты</h2>
          <p>Созданные изображения, тексты и промпты сохраняются здесь.</p>
        </div>
      </div>
      <div className="gallery-header-bar">
        <div className="gallery-filter-tabs" role="group" aria-label="Фильтр результатов">
          {(["all", "image", "text", "prompt"] as const).map((t) => (
            <button
              key={t}
              aria-pressed={filter === t}
              className={`gallery-filter-button ${filter === t ? "active" : ""}`}
              onClick={() => setFilter(t)}
            >
              {t === "all" ? "Все" : t === "image" ? "Изображения" : t === "text" ? "Тексты" : "Промпты"}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <SkeletonGrid />
      ) : filteredTasks.length === 0 ? (
        <div className="empty-state" style={{ padding: "40px 16px", textAlign: "center" }}>
          <GalleryHorizontalEnd size={32} style={{ color: "var(--muted)", marginBottom: "12px" }} />
          <strong>Здесь пока пусто</strong>
          <p style={{ color: "var(--muted)", fontSize: "14px", margin: "8px 0 16px 0" }}>Здесь будут собраны созданные вами файлы.</p>
          <button className="primary gallery-empty-action" onClick={onCreate}>Создать первый результат</button>
        </div>
      ) : (
        <div className="gallery-grid">
          {filteredTasks.map((task) => {
            const capabilityStr = typeof task.capability === "string" ? task.capability : "";
            const isImage = capabilityStr.startsWith("image.");
            const isText = capabilityStr.startsWith("text.");
            const asset = task.assets?.[0];
            const previewUrl = apiUrl(asset?.preview_url);
            const downloadUrl = apiUrl(asset?.download_url || asset?.preview_url);
            const resultFrameLabel = taskAspectRatioLabel(task);
            const resultFrameRatio = taskAspectRatio(task);
            const textResult = sanitizeTextPayload(asset?.metadata?.text);
            const modelLabel = getPublicModelLabel(
              capabilityStr,
              task.model_id,
              task.model_name,
              isImage ? IMAGE_MODEL_LABEL : TEXT_MODEL_LABEL,
              generationUsesAgy,
            );

            return (
              <article className="result-card" key={task.id}>
                <div className="result-visual" style={{ aspectRatio: resultFrameRatio }} data-ratio={resultFrameLabel}>
                  {task.status === "completed" ? (
                    isImage && previewUrl ? (
                      <AuthorizedImage source={previewUrl} fallbackSource={downloadUrl} alt={task.prompt} loading="lazy" />
                    ) : isText && textResult ? (
                      <div style={{ padding: "16px", fontSize: "12px", width: "100%", height: "100%", overflowY: "auto", whiteSpace: "pre-wrap", color: "var(--muted)" }}>
                        {textResult}
                      </div>
                    ) : (
                      <div className="visual-placeholder">
                        {isImage ? <ImageIcon size={24} aria-hidden="true" /> : isText ? <FileText size={24} aria-hidden="true" /> : <GalleryHorizontalEnd size={24} aria-hidden="true" />}
                        <span>Результат готов</span>
                        <small>{isImage ? "Предпросмотр пока недоступен" : textResult ? "Откройте карточку, чтобы прочитать текст" : "Откройте карточку для подробностей"}</small>
                      </div>
                    )
                  ) : task.status === "failed" ? (
                    <div className="visual-error">
                      <span style={{ fontSize: "28px" }}>⚠️</span>
                      <small style={{ color: "var(--rose)" }}>Ошибка</small>
                    </div>
                  ) : task.status === "cancelled" ? (
                    <div className="visual-error">
                      <span style={{ fontSize: "28px" }}>🚫</span>
                      <small style={{ color: "var(--muted)" }}>Отменено</small>
                    </div>
                  ) : task.status === "refunded" ? (
                    <div className="visual-error" style={{ background: "rgba(6, 182, 212, 0.05)" }}>
                      <span style={{ fontSize: "28px" }}>↩️</span>
                      <small style={{ color: "var(--teal)" }}>Возврат</small>
                    </div>
                  ) : (
                    <div className="visual-loader">
                      <Loader2 className="spin" size={24} />
                      <span>Создаётся...</span>
                    </div>
                  )}
                  <span className="result-badge">
                    {getTaskTypeLabel(capabilityStr)}
                  </span>
                </div>

                <div className="result-body">
                  <strong>{modelLabel}</strong>
                  <p>{task.prompt || "Без описания"}</p>
                  <span className="result-date">{formatDate(task.created_at)}</span>
                  <span className="cost-badge">{formatTaskCost(task)}</span>
                  {task.error_message && <small style={{ color: "var(--rose)" }}>{task.error_message}</small>}
                </div>

                <div className="result-actions">
                  <button className="secondary" onClick={() => onOpen(task)}>
                    Открыть
                  </button>
                  <button className="secondary" onClick={() => onReuse(task)}>
                    Повторить
                  </button>
                  {isImage && previewUrl && task.status === "completed" && (
                    <button className="secondary" onClick={() => onUseAsReference(task)}>
                      Как образец
                    </button>
                  )}
                  {isImage && downloadUrl && task.status === "completed" && (
                    <SaveImageButton source={downloadUrl} filename={`znich_${task.id}.png`} />
                  )}
                  <button className="delete-btn" aria-label="Удалить" title="Удалить" onClick={() => onDelete(task.id)}>
                    <Trash2 size={16} />
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {selectedTask && (
        <div className="modal-overlay result-modal-overlay" onClick={onCloseDetail}>
          <div className="modal-content result-modal-content" role="dialog" aria-modal="true" aria-labelledby="result-dialog-title" onClick={(e) => e.stopPropagation()}>
            <ResultDetail
              task={selectedTask}
              onClose={onCloseDetail}
              onReuse={onReuse}
              onUseAsReference={onUseAsReference}
              onAction={onAction}
              onDelete={onDelete}
            />
          </div>
        </div>
      )}
    </section>
  );
}

async function saveAuthorizedAsset(source: string, filename: string): Promise<"shared" | "downloaded"> {
  const response = await fetch(source, { headers: new Headers(getAuthHeaders()) });
  if (!response.ok) throw new Error(`asset-${response.status}`);
  const blob = await response.blob();
  if (!blob.size) throw new Error("asset-empty");

  const file = new File([blob], filename, { type: blob.type || "image/png" });
  const shareNavigator = navigator as Navigator & {
    canShare?: (data?: ShareData) => boolean;
    share?: (data?: ShareData) => Promise<void>;
  };
  const canShareFile = typeof shareNavigator.share === "function"
    && (!shareNavigator.canShare || shareNavigator.canShare({ files: [file] }));
  if (canShareFile) {
    try {
      await shareNavigator.share({ files: [file], title: "Изображение Znich" });
      return "shared";
    } catch (error) {
      // A cancelled share should close quietly; unsupported share targets can
      // still use the regular download fallback below.
      if (error instanceof DOMException && error.name === "AbortError") throw error;
    }
  }

  const objectUrl = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = filename;
    anchor.rel = "noopener";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  }
  return "downloaded";
}

function SaveImageButton({ source, filename }: { source: string; filename: string }) {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [failed, setFailed] = useState(false);

  async function handleSave() {
    if (saving) return;
    setSaving(true);
    setFailed(false);
    try {
      await saveAuthorizedAsset(source, filename);
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2600);
    } catch (error) {
      if (!(error instanceof DOMException && error.name === "AbortError")) setFailed(true);
      window.setTimeout(() => setFailed(false), 3200);
    } finally {
      setSaving(false);
    }
  }

  return (
    <button
      type="button"
      className="secondary save-image-button"
      onClick={() => void handleSave()}
      disabled={saving}
      aria-label="Сохранить изображение"
      title={failed ? "Не удалось сохранить. Нажмите ещё раз" : "Сохранить изображение в фото или файлы"}
    >
      {saving ? <Loader2 className="spin" size={15} /> : saved ? <CircleCheck size={15} /> : (typeof navigator !== "undefined" && "share" in navigator ? <Share2 size={15} /> : <Download size={15} />)}
      <span>{saving ? "Сохраняем…" : saved ? "Сохранено" : failed ? "Повторить" : "Сохранить"}</span>
    </button>
  );
}

function ResultDetail({
  task,
  onClose,
  onReuse,
  onUseAsReference,
  onAction,
  onDelete
}: {
  task: Task;
  onClose: () => void;
  onReuse: (task: Task) => void;
  onUseAsReference: (task: Task) => void;
  onAction: (task: Task, action: string) => void;
  onDelete: (taskId: string) => void;
}) {
  const capabilityStr = typeof task.capability === "string" ? task.capability : "";
  const isImage = capabilityStr.startsWith("image.");
  const isPromptBuilder = capabilityStr === "text.prompt_builder";
  const asset = task.assets?.[0];
  const previewUrl = apiUrl(asset?.preview_url);
  const downloadUrl = apiUrl(asset?.download_url || asset?.preview_url);
  // The gallery uses the lightweight preview, while the detail view should
  // show the full-size file whenever it is available. Both URLs are fetched
  // with Telegram auth by AuthorizedImage.
  const detailImageUrl = downloadUrl || previewUrl;
  const detailFrameLabel = taskAspectRatioLabel(task);
  const detailFrameRatio = taskAspectRatio(task);
  const textResult = sanitizeTextPayload(asset?.metadata?.text);
  const modelLabel = getPublicModelLabel(
    capabilityStr,
    task.model_id,
    task.model_name,
    isImage ? IMAGE_MODEL_LABEL : TEXT_MODEL_LABEL,
    generationUsesAgy,
  );
  const [availableActions, setAvailableActions] = useState<ResultAction[]>([]);
  const [copiedText, setCopiedText] = useState(false);

  useEffect(() => {
    let active = true;
    void fetchTaskActions(task.id)
      .then((result) => {
        if (active) setAvailableActions(Array.isArray(result.actions) ? result.actions : []);
      })
      .catch(() => {
        if (active) setAvailableActions([]);
      });
    return () => {
      active = false;
    };
  }, [task.id]);

  async function copyTextResult() {
    if (!textResult || !navigator.clipboard?.writeText) return;
    try {
      await navigator.clipboard.writeText(textResult);
      setCopiedText(true);
      window.setTimeout(() => setCopiedText(false), 1800);
    } catch {
      // Clipboard permissions can be unavailable in embedded webviews.
    }
  }

  const formattedCost = useMemo(() => {
    const tokens = Number(task.actual_tokens || task.estimated_tokens || 0);
    return tokens > 0 ? `${tokens.toLocaleString()} токенов` : "Стоимость уточняется";
  }, [task.actual_tokens, task.estimated_tokens]);

  return (
    <article className="result-detail panel" style={{ border: "none", background: "transparent", padding: 0 }}>
      <div className="section-heading" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
        <div>
          <span className="eyebrow">Результат</span>
          <h2 id="result-dialog-title">{getTaskTypeLabel(capabilityStr)}</h2>
        </div>
        <button className="secondary compact-button" onClick={onClose}>Закрыть</button>
      </div>
      <div className="detail-layout">
        <div
          className={`detail-preview ${isImage ? "detail-preview-image" : ""}`}
          style={isImage ? { aspectRatio: detailFrameRatio, height: "auto", minHeight: 0 } : undefined}
          data-ratio={isImage ? detailFrameLabel : undefined}
        >
          {isImage && detailImageUrl ? (
            <AuthorizedImage source={detailImageUrl} alt={task.prompt || "Результат"} loading="eager" />
          ) : textResult ? (
            <pre style={{ maxHeight: "300px", overflowY: "auto" }}>{textResult}</pre>
          ) : (
            <span>
              {task.status === "failed"
                ? "Не удалось открыть результат"
                : task.status === "completed"
                  ? "Предпросмотр недоступен"
                  : "Файл создаётся…"}
            </span>
          )}
        </div>
        <div className="detail-meta">
          <span>Модель</span>
          <strong>{modelLabel}</strong>
          <span>Дата</span>
          <strong>{formatDate(task.created_at)}</strong>
          <span>Стоимость</span>
          <strong>{formattedCost}</strong>
          <span>Описание</span>
          <p>{task.prompt || "Без описания"}</p>
          {task.error_message && <p className="safe-error" style={{ color: "var(--rose)" }}>{task.error_message}</p>}
        </div>
      </div>
      <div className="detail-actions" style={{ marginTop: "20px", display: "flex", flexWrap: "wrap", gap: "8px" }}>
        <button className="secondary" onClick={() => onReuse(task)}>Повторить</button>
        {isImage && previewUrl && task.status === "completed" && (
          <button className="secondary" onClick={() => onUseAsReference(task)}>Использовать как образец</button>
        )}
        {isImage && downloadUrl && task.status === "completed" && (
          <SaveImageButton source={downloadUrl} filename={`znich_${task.id}.png`} />
        )}
        {!isImage && textResult && task.status === "completed" && (
          <button className="secondary" onClick={() => void copyTextResult()}><Copy size={15} />{copiedText ? "Скопировано" : "Скопировать"}</button>
        )}
        {task.status === "completed" && availableActions
          .filter((item) => ["improve", "shorten", "translate", "continue", "use_as_prompt", "remove_background"].includes(item.action))
          .map((item) => (
            <button className="secondary" key={item.action} onClick={() => onAction(task, item.action)}>
              {item.title}
            </button>
          ))}
        <button className="delete-btn" onClick={() => {
          if (confirm("Удалить этот результат из галереи?")) {
            onDelete(task.id);
            onClose();
          }
        }} style={{ marginLeft: "auto" }}>
          <Trash2 size={16} /> Удалить
        </button>
      </div>
    </article>
  );
}function BalanceScreen({ balance, transactions, loading }: { balance: string | null; transactions: Transaction[]; loading: boolean }) {
  const [showAll, setShowAll] = useState(false);

  if (loading) {
    return <SkeletonList />;
  }

  const botUrl = import.meta.env.VITE_TELEGRAM_BOT_URL || "https://t.me/ZnichBot";
  const topupUrl = `${botUrl}?start=topup`;

  const classifyTx = (tx: Transaction): "debit" | "credit" | "refund" => {
    const type = (tx.transaction_type || "").toLowerCase();
    const desc = (tx.description || "").toLowerCase();
    
    const balanceDiff = Number(tx.balance_after) - Number(tx.balance_before);
    if (!isNaN(balanceDiff) && balanceDiff !== 0) {
      if (balanceDiff < 0) return "debit";
      if (type.includes("refund") || desc.includes("возврат") || desc.includes("refund") || type.includes("return")) return "refund";
      return "credit";
    }

    if (
      type.includes("debit") || type.includes("charge") || type.includes("payment") ||
      type.includes("task") || type.includes("generation") || type.includes("spend") ||
      desc.includes("списан") || desc.includes("генераци")
    ) return "debit";
    if (
      type.includes("refund") || type.includes("return") || type.includes("возврат") ||
      desc.includes("возврат") || desc.includes("refund")
    ) return "refund";
    if (Number(tx.amount) < 0) return "debit";
    return "credit";
  };

  const visibleTxs = showAll ? transactions : transactions.slice(0, 8);

  return (
    <section className="balance-screen">
      <div className="balance-hero">
        <Coins size={32} />
        <span>Баланс</span>
        <strong>{balance === null ? "Не удалось загрузить" : formatTokens(balance, { exact: true })}</strong>
        <p>
          {balance === null
            ? "Откройте Znich в Telegram, чтобы увидеть свой баланс."
            : "Баланс списывается только после успешного выполнения задачи."}
        </p>
        <a
          href={topupUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{ display: "inline-flex", alignItems: "center", gap: "8px", textDecoration: "none", padding: "10px 20px", borderRadius: "8px", fontWeight: "bold", background: "var(--teal)", color: "black", fontSize: "14px" }}
        >
          <Bot size={16} /> Пополнить баланс
        </a>
      </div>

      <div className="panel">
        <h3 style={{ margin: "0 0 12px 0", fontSize: "15px" }}>История операций</h3>
        {transactions.length === 0 ? (
          <EmptyState
            icon={<Coins size={24} />}
            title="История транзакций пуста"
            text="Здесь появятся расходы на создание контента и пополнения баланса."
          />
        ) : (
          <>
            <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              {visibleTxs.map((tx) => {
                const kind = classifyTx(tx);
                const amountVal = Math.abs(Number(tx.amount));
                // Use server-formatted amount if available, otherwise format locally
                const amountFormatted = tx.amount_formatted
                  ? tx.amount_formatted.replace(/^[-−~]/, "")  // strip sign/prefix from server format
                  : formatTokens(amountVal, { exact: true }).replace(/ токен(а|ов)?$/, "").trim();
                const amountColor = kind === "debit" ? "var(--rose)" : kind === "refund" ? "var(--teal)" : "var(--green)";
                const amountSign = kind === "debit" ? "−" : "+";
                const defaultDesc = kind === "debit" ? "Создание контента" : kind === "refund" ? "Возврат" : "Пополнение";

                return (
                  <div key={tx.id} className="transaction-row">
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0 }}>
                      <div
                        style={{
                          width: "24px",
                          height: "24px",
                          borderRadius: "50%",
                          background: kind === "debit" ? "rgba(244,63,94,0.12)" : kind === "refund" ? "rgba(6,182,212,0.12)" : "rgba(16,185,129,0.12)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          fontSize: "12px"
                        }}
                      >
                        {kind === "debit" ? "↓" : kind === "refund" ? "↺" : "↑"}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--foreground)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {getPublicTransactionDescription(tx.description) || defaultDesc}
                        </div>
                        <div style={{ fontSize: "11px", color: "var(--muted)", marginTop: "1px" }}>
                          {formatDate(tx.created_at)}
                        </div>
                      </div>
                    </div>
                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                      <div style={{ fontWeight: 700, color: amountColor, fontSize: "14px", fontVariantNumeric: "tabular-nums" }}>
                        {amountSign}{amountFormatted}
                      </div>
                      <div style={{ fontSize: "10px", color: "var(--muted-dark)", marginTop: "1px" }}>
                        {Math.round(Number(tx.balance_after)).toLocaleString()}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {transactions.length > 8 && (
              <button
                className="secondary"
                onClick={() => setShowAll(!showAll)}
                style={{ width: "100%", marginTop: "8px", fontSize: "13px", minHeight: "36px" }}
              >
                {showAll ? "Свернуть" : `Показать ещё ${transactions.length - 8} операций`}
              </button>
            )}
          </>
        )}
      </div>
    </section>
  );
}

function SettingsScreen({
  runtime,
  authStatus,
  profile
}: {
  runtime: string;
  authStatus: AuthStatus;
  profile: { username?: string | null; fullName?: string | null };
}) {
  const displayName = profile.fullName || (profile.username ? `@${profile.username}` : authStatus === "authenticated" ? "Пользователь Telegram" : "Войдите через Telegram");
  const [notifyBot, setNotifyBot] = useState(true);
  const [notifyBilling, setNotifyBilling] = useState(true);
  const [lang, setLang] = useState("ru");

  return (
    <section className="settings-screen">
      <div className="panel">
        <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
          <User size={20} style={{ color: "var(--teal)" }} />
          <h2 style={{ margin: 0, fontSize: "18px" }}>Профиль</h2>
        </div>
        <div className="settings-list" style={{ display: "grid", gridTemplateColumns: "130px 1fr", gap: "12px 8px", fontSize: "14px", alignItems: "center" }}>
          <span style={{ color: "var(--muted)" }}>Имя:</span>
          <strong>{displayName}</strong>

          <span style={{ color: "var(--muted)" }}>Авторизация:</span>
          <strong>{authStatus === "authenticated" ? "Подключено через Telegram" : "Режим просмотра"}</strong>

          <span style={{ color: "var(--muted)" }}>Платформа:</span>
          <strong>{runtime === "telegram_mini_app" ? "Telegram Mini App" : "Веб-версия"}</strong>
        </div>
      </div>

      <div className="panel">
        <h3 style={{ margin: "0 0 16px 0", fontSize: "16px" }}>Уведомления</h3>
        <div style={{ display: "grid", gap: "12px" }}>
          <label style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer" }}>
            <input type="checkbox" checked={notifyBot} onChange={(e) => setNotifyBot(e.target.checked)} />
            <div style={{ display: "grid" }}>
              <strong>Готовые файлы в Telegram</strong>
              <small style={{ color: "var(--muted)", fontSize: "12px" }}>Получайте созданные файлы в чате Telegram</small>
            </div>
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer" }}>
            <input type="checkbox" checked={notifyBilling} onChange={(e) => setNotifyBilling(e.target.checked)} />
            <div style={{ display: "grid" }}>
              <strong>Оповещения о балансе</strong>
              <small style={{ color: "var(--muted)", fontSize: "12px" }}>Сообщения об оплате и пополнении баланса</small>
            </div>
          </label>
        </div>
      </div>

      <div className="panel">
        <h3 style={{ margin: "0 0 12px 0", fontSize: "16px" }}>Язык</h3>
        <div className="segmented" style={{ display: "flex", gap: "4px", background: "var(--bg-dark)", padding: "4px", borderRadius: "8px", maxWidth: "200px" }}>
          <button aria-pressed={lang === "ru"} className={lang === "ru" ? "active" : ""} onClick={() => setLang("ru")} style={{ flex: 1, padding: "6px", border: "none", borderRadius: "6px", background: lang === "ru" ? "var(--bg-card)" : "transparent", color: "var(--foreground)", cursor: "pointer", fontSize: "13px" }}>Русский</button>
          <button aria-pressed={lang === "en"} className={lang === "en" ? "active" : ""} onClick={() => setLang("en")} style={{ flex: 1, padding: "6px", border: "none", borderRadius: "6px", background: lang === "en" ? "var(--bg-card)" : "transparent", color: "var(--foreground)", cursor: "pointer", fontSize: "13px" }}>English</button>
        </div>
      </div>

      <div className="panel" style={{ padding: "16px" }}>
        <details>
          <summary style={{ cursor: "pointer", fontWeight: "bold", fontSize: "14px", color: "var(--muted)" }}>Конфиденциальность</summary>
          <p style={{ color: "var(--muted)", fontSize: "13px", lineHeight: "1.6", marginTop: "10px" }}>
            Черновики и созданные файлы привязаны к вашему аккаунту Telegram. Вы можете удалить любую работу из галереи. Файлы хранятся на сервере Znich.
          </p>
        </details>
      </div>

      <div style={{ textAlign: "center", marginTop: "24px" }}>
        <a href="https://t.me/vladkas" target="_blank" rel="noopener noreferrer" style={{ color: "var(--teal)", textDecoration: "none", fontSize: "14px" }}>
          Написать в поддержку
        </a>
      </div>
    </section>
  );
}

function DesktopNav({ screen, onChange }: { screen: Screen; onChange: (screen: Screen) => void }) {
  return <NavList screen={screen} onChange={onChange} variant="desktop" />;
}

function MobileNav({ screen, onChange }: { screen: Screen; onChange: (screen: Screen) => void }) {
  return <NavList screen={screen} onChange={onChange} variant="mobile" />;
}

function NavList({
  screen,
  onChange,
  variant
}: {
  screen: Screen;
  onChange: (screen: Screen) => void;
  variant: "desktop" | "mobile";
}) {
  const items: Array<[Screen, string, ReactNode]> = [
    ["create", "Создать", <Wand2 size={18} />],
    ["gallery", "Галерея", <GalleryHorizontalEnd size={18} />],
    ["balance", "Баланс", <Coins size={18} />],
    ["settings", "Профиль", <Settings size={18} />]
  ];
  return (
    <nav className={`nav-list ${variant}`}>
      {items.map(([id, label, icon]) => (
        <button key={id} className={screen === id ? "active" : ""} aria-current={screen === id ? "page" : undefined} onClick={() => onChange(id)}>
          {icon}
          <span>{label}</span>
        </button>
      ))}
    </nav>
  );
}

function ControlGroup({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="control-group">
      <span>{title}</span>
      {children}
    </div>
  );
}

function EmptyState({ icon, title, text }: { icon: ReactNode; title: string; text: string }) {
  return (
    <div className="empty-state">
      {icon}
      <strong>{title}</strong>
      <p>{text}</p>
    </div>
  );
}

type ScreenErrorBoundaryProps = {
  children: ReactNode;
  onReset: () => void;
};

type ScreenErrorBoundaryState = {
  hasError: boolean;
};

class ScreenErrorBoundary extends Component<ScreenErrorBoundaryProps, ScreenErrorBoundaryState> {
  state: ScreenErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ScreenErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Keep the production UI useful while retaining diagnostics in the
    // browser console for the next report.
    console.error("Create workspace screen failed to render", error, info.componentStack);
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <section className="screen-error panel" role="alert">
        <GalleryHorizontalEnd size={28} aria-hidden="true" />
        <strong>Не удалось открыть этот раздел</strong>
        <p>Попробуйте открыть другой раздел или вернуться к созданию. Ваши работы и баланс не потерялись.</p>
        <button className="primary" onClick={this.props.onReset}>Вернуться к созданию</button>
      </section>
    );
  }
}

function SkeletonList() {
  return (
    <section className="panel skeleton-panel" aria-label="Загрузка">
      <div className="skeleton-line wide" />
      <div className="skeleton-line" />
      <div className="skeleton-line short" />
      <div className="skeleton-card" />
    </section>
  );
}

function SkeletonGrid() {
  return (
    <section className="gallery-grid" aria-label="Загрузка галереи">
      {[0, 1, 2, 3].map((item) => (
        <div className="result-card skeleton-result" key={item}>
          <div className="skeleton-image" />
          <div className="result-body">
            <div className="skeleton-line" />
            <div className="skeleton-line short" />
          </div>
        </div>
      ))}
    </section>
  );
}

function Notice({ tone, text }: { tone: "info" | "error"; text: string }) {
  return <div className={`notice ${tone}`} role={tone === "error" ? "alert" : "status"}>{text}</div>;
}

function AuthBanner({ runtime, authStatus }: { runtime: string; authStatus: AuthStatus }) {
  const botUrl = import.meta.env.VITE_TELEGRAM_BOT_URL || "https://t.me/ZnichBot";
  return (
    <div className="auth-banner">
      <span>
        {authStatus === "checking"
          ? "Проверяем вход через Telegram..."
          : runtime === "standalone_web"
            ? "Вы открыли Znich в обычном браузере. Чтобы создавать контент, сохранять историю и управлять балансом, запустите приложение в Telegram."
            : "Не удалось войти через Telegram. Перезапустите приложение из бота."}
      </span>
      {runtime === "standalone_web" && (
        <a className="auth-link" href={botUrl} target="_blank" rel="noopener noreferrer">
          Открыть в Telegram
        </a>
      )}
    </div>
  );
}

function formatDate(value?: string | null) {
  if (!value) return "Дата неизвестна";
  try {
    const date = new Date(value);
    if (isNaN(date.getTime())) return "Дата неизвестна";
    return new Intl.DateTimeFormat("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit"
    }).format(date);
  } catch {
    return "Дата неизвестна";
  }
}

/**
 * Format token amounts with proper Russian plural forms.
 * 1 → «1 токен», 2–4 → «2 токена», 5+ → «5 токенов»
 * Small amounts get 1 decimal place.
 */
function formatTokens(amount: string | number | null | undefined, opts?: { exact?: boolean; prefix?: string }): string {
  if (amount === null || amount === undefined) return "0 токенов";
  const value = Number(amount);
  if (isNaN(value) || value === 0) return "0 токенов";

  const prefix = opts?.prefix ?? (opts?.exact ? "" : "~");

  const plural = (n: number): string => {
    const abs = Math.abs(n);
    const lastTwo = Math.floor(abs) % 100;
    const lastOne = Math.floor(abs) % 10;
    if (lastTwo >= 11 && lastTwo <= 19) return "токенов";
    if (lastOne === 1) return "токен";
    if (lastOne >= 2 && lastOne <= 4) return "токена";
    return "токенов";
  };

  const absValue = Math.abs(value);
  let formatted: string;
  if (absValue < 1) {
    formatted = absValue.toFixed(1);
  } else if (absValue < 100) {
    const dec = parseFloat(absValue.toFixed(1));
    formatted = dec === Math.floor(dec) ? String(Math.round(dec)) : dec.toFixed(1);
  } else {
    formatted = Math.round(absValue).toLocaleString("ru-RU");
  }

  return `${prefix}${formatted} ${plural(value)}`;
}

function getCapabilityTitle(value: Capability) {
  if (value === "image.text_to_image") return "Изображение по описанию";
  if (value === "image.image_to_image") return "Изображение по образцу";
  if (value === "text.prompt_builder") return "Промпты";
  if (value.startsWith("text.")) return "Текст";
  if (value.startsWith("audio.")) return "Аудио";
  return "Создание";
}

function getResultTypeTitle(value: ResultType) {
  return resultTypeDefinitions.find((type) => type.id === value)?.title || "Создание";
}

function formatQualityLabel(value: string) {
  return value === "hd" ? "Высокое (HD)" : "Стандартное";
}

function getTaskTypeLabel(capability?: string | null) {
  const value = typeof capability === "string" ? capability : "";
  if (!value) return "Результат";
  if (value.startsWith("image.")) return "Изображение";
  if (value.startsWith("video.")) return "Видео";
  if (value.startsWith("audio.")) return "Аудио";
  if (value === "text.prompt_builder") return "Промпт";
  if (value.startsWith("text.")) return "Текст";
  return "Результат";
}

function formatTaskCost(task: Task) {
  const value = Number(task.actual_tokens || task.estimated_tokens || 0);
  return value > 0 ? formatTokens(value) : "Стоимость уточняется";
}

function placeholderForScenario(value: ScenarioId) {
  const placeholders: Record<ScenarioId, string> = {
    "image.text_to_image": "Например: ночной рынок под дождем, теплый неон, кинематографичный свет, реалистичная детализация",
    "image.image_to_image": "Например: сохраните композицию, сделайте свет теплее, добавьте ощущение утреннего города",
    "text.write": "Например: напиши короткий пост о запуске сервиса в уверенном, спокойном тоне",
    "text.improve": "Вставьте текст и напишите, что улучшить: ясность, тон, структуру, убедительность",
    "text.shorten": "Вставьте текст и укажите формат: короткое резюме, тезисы, 5 пунктов, пост",
    "text.translate": "Вставьте текст и укажите язык перевода, если нужен не английский",
    "text.continue": "Вставьте начало текста и опишите, как продолжить",
    "prompt.universal": "Например: точный запрос для аватара в стиле киберпанк",
    "prompt.image": "Например: минималистичный продуктовый кадр для премиального сервиса, тёмный фон, тёплый свет",
    "prompt.flux": "Например: портрет девушки в неоновом свете, высокая детализация кожи, объектив 85мм",
    "prompt.video": "Например: короткий ролик о запуске продукта, плавное движение камеры, 5 секунд, вертикальный формат",
    "prompt.json_image": "Например: главный экран приложения в тёмном премиальном стиле",
    "prompt.json_video": "Например: сцена пролета камеры над футуристическим городом с неоновой рекламой",
    "prompt.research": "Например: исследовать рынок сервисов для создания изображений в Telegram",
    "video.text_to_video": "Например: пролет камеры над неоновым Токио, плавно, 4k",
    "video.image_to_video": "Например: оживи воду, сделай волны мягко перекатывающимися",
    "audio.text_to_speech": "Введите текст, который нужно озвучить",
    "audio.speech_to_speech": "Загрузите голосовой файл и опишите желаемую подачу",
    "audio.transcription": "Загрузите аудиофайл для расшифровки"
  };
  return placeholders[value];
}

function formatParameterName(value: string) {
  const labels: Record<string, string> = {
    aspect_ratio: "Формат",
    quality: "Качество",
    size: "Размер",
    n: "Количество вариантов",
    prompt: "Описание",
    negative_prompt: "Что не добавлять",
    max_tokens: "Длина ответа",
    temperature: "Свобода ответа",
    style: "Стиль"
  };
  return labels[value] || value.replaceAll("_", " ");
}

function formatParameterSummaryName(value: string) {
  if (value === "n") return "Варианты";
  return formatParameterName(value);
}

function systemPromptForTone(tone: string, scenario?: ScenarioId) {
  const base = [
    "Отвечай на русском языке, если пользователь не попросил другой язык.",
    "Пиши только финальный пользовательский результат без рассуждений, планов и технических пояснений.",
    "Сохраняй ясность, аккуратную структуру и естественный тон."
  ];
  const toneRules: Record<string, string> = {
    "Дружелюбно": "Тон: дружелюбный, теплый, без фамильярности.",
    "Уверенно": "Тон: уверенный, деловой, с ясными формулировками.",
    "Коротко": "Тон: краткий. Убери лишние вводные и оставь только суть.",
    "Подробно": "Тон: подробный. Дай больше контекста, примеров и структуры.",
    "Нейтрально": "Тон: нейтральный и спокойный."
  };
  const scenarioRules: Partial<Record<ScenarioId, string>> = {
    "text.improve": "Задача: улучшить исходный текст, сохранив смысл и факты.",
    "text.shorten": "Задача: сократить исходный текст без потери ключевого смысла.",
    "text.translate": "Задача: перевести текст, сохранив смысл, тон и формат.",
    "text.continue": "Задача: продолжить исходный текст в том же стиле.",
    "text.write": "Задача: написать новый текст по описанию пользователя."
  };
  return [...base, toneRules[tone] || toneRules["Нейтрально"], scenario ? scenarioRules[scenario] : null].filter(Boolean).join(" ");
}
